import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { auth } from '../firebase';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  sendEmailVerification
} from 'firebase/auth';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { getDoc, getDocs, collection, query, where, onSnapshot, deleteField } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import firebasePhoneAuthService, { fireConfirmPhoneVerified } from '../services/firebasePhoneAuth';
import PlacesAutocomplete, { geocodeByAddress, getLatLng } from 'react-places-autocomplete';
import imageCompression from 'browser-image-compression';
import { isUsernameBlocklisted } from '../utils/usernameBlocklist';
import { Capacitor } from '@capacitor/core';
import { getGoogleMapsApiKey } from '../services/googleMaps';
import { scrollPageToTop } from '../utils/scrollPageToTop';
import {
  getBiometricLabel,
  hasSavedBiometricLogin,
  saveBiometricLogin,
  getBiometricLogin,
  clearBiometricLogin,
  maybePromptSavePasswordToKeychain,
  getSavedBiometricEmail
} from '../services/biometricLogin';

// Origin used to build Firebase email action-link continue URLs. On native the
// page origin is capacitor://localhost, which the Admin SDK rejects when calling
// generateEmailVerificationLink (so the branded email send fails and the flow
// falls back to Firebase's default raw-link email), and which can't be opened in
// a browser anyway. Use the real production web origin on native; keep the live
// origin on web (localhost in dev, https://pedalxchange.com in prod).
const getEmailLinkOrigin = () =>
  Capacitor.isNativePlatform() ? 'https://pedalxchange.com' : window.location.origin;

// Native finishes verification in an external browser, so it returns via the
// /email-verified handoff page (which deep-links back into the app). Web stays
// on the existing in-place signup flow that auto-advances when emailVerified=true.
const getEmailContinueUrl = () =>
  Capacitor.isNativePlatform()
    ? 'https://pedalxchange.com/email-verified'
    : `${window.location.origin}/signup?emailVerified=true`;

// Last account signed in on this device, used to prefill the login email so
// returning users (web and native) don't retype it.
const LAST_LOGIN_EMAIL_KEY = 'pxc_last_login_email';

const readLastLoginEmail = () => {
  try {
    return localStorage.getItem(LAST_LOGIN_EMAIL_KEY) || '';
  } catch {
    return '';
  }
};

const rememberLastLoginEmail = (value) => {
  try {
    localStorage.setItem(LAST_LOGIN_EMAIL_KEY, value);
  } catch {
    // best-effort only
  }
};

const AuthForm = ({ isSignUp: initialIsSignUp = false }) => {
  const navigate = useNavigate();
  
  // 🔐 Auth states
  const [email, setEmail] = useState(() => (initialIsSignUp ? '' : readLastLoginEmail()));
  const [password, setPassword] = useState('');
  const [isSignUp, setIsSignUp] = useState(initialIsSignUp);
  const [error, setError] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const [resetSending, setResetSending] = useState(false);

  // Email we filled in for the user (vs. one they typed), so switching to the
  // sign-up form can clear it without discarding real input.
  const prefilledEmailRef = useRef(initialIsSignUp ? '' : readLastLoginEmail());

  // Returning to the login form (e.g. after toggling to sign-up) re-fills the
  // remembered account, but never overwrites something already typed.
  useEffect(() => {
    if (isSignUp) return;
    const last = readLastLoginEmail();
    if (!last) return;
    setEmail((current) => {
      if (current) return current;
      prefilledEmailRef.current = last;
      return last;
    });
  }, [isSignUp]);

  // Phones get extra breathing room above the login buttons.
  const [isNarrowScreen, setIsNarrowScreen] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 640px)').matches
  );
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const mq = window.matchMedia('(max-width: 640px)');
    const onChange = (e) => setIsNarrowScreen(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  // 🔒 Native saved-login (Face ID / Touch ID) states
  const [bioLabel, setBioLabel] = useState(null); // 'Face ID' | 'Touch ID' | 'biometrics' | null
  const [bioSavedLogin, setBioSavedLogin] = useState(false);
  const [bioSavedEmail, setBioSavedEmail] = useState(null);
  const [biometricLoggingIn, setBiometricLoggingIn] = useState(false);

  // Native app only: check whether biometrics are available and a login was
  // previously saved, so the login form can offer "Log in with Face ID".
  useEffect(() => {
    if (isSignUp || !Capacitor.isNativePlatform()) return undefined;
    let cancelled = false;
    (async () => {
      const label = await getBiometricLabel();
      const saved = label ? await hasSavedBiometricLogin() : false;
      if (!cancelled) {
        setBioLabel(label);
        setBioSavedLogin(saved);
        setBioSavedEmail(saved ? getSavedBiometricEmail() : null);
      }
    })();
    return () => { cancelled = true; };
  }, [isSignUp]);

  // Only one login is stored for Face ID (the most recent password login), so
  // show which account the button signs into, with the inbox partly masked:
  // "testpedal8@gmail.com" -> "tes•••@gmail.com".
  const maskEmail = (addr) => {
    const at = addr.indexOf('@');
    if (at <= 0) return addr;
    const visible = addr.slice(0, Math.min(3, at));
    return `${visible}•••${addr.slice(at)}`;
  };

  // 🎯 Signup flow states
  const [currentStep, setCurrentStep] = useState(1); // 1: email/password, 2: email verification, 3: phone, 4: profile info
  const [emailSent, setEmailSent] = useState(false);
  const [isCreatingAccount, setIsCreatingAccount] = useState(false);
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);

  // Each signup step is effectively a new page — start it at the top.
  // Without this, the scroll position from the previous step carries over
  // (e.g. arriving at the SMS step already scrolled to the middle).
  // NOTE: must stay below the currentStep declaration above.
  // Every signup step is text fields, so the keyboard is up when the user
  // advances — scrolling synchronously here lost to iOS restoring its own offset
  // after the keyboard dismissal animation. See scrollPageToTop.
  useEffect(() => {
    if (!isSignUp) return undefined;
    return scrollPageToTop();
  }, [currentStep, isSignUp]);

  // 🔄 Update internal state when prop changes (for navigation between /signup and /login)
  useEffect(() => {
    setIsSignUp(initialIsSignUp);
    // Reset form state when switching between login/signup via navigation
    if (!initialIsSignUp) {
      setCurrentStep(1);
      setEmailSent(false);
      setEmailVerified(false);
      setPhoneVerified(false);
      setRegistrationComplete(false);
      setError('');
      setShowForgotPassword(false);
      setResetEmailSent(false);
    }
  }, [initialIsSignUp]);
  const [emailVerified, setEmailVerified] = useState(false);
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [registrationComplete, setRegistrationComplete] = useState(false);
  const emailVerificationProcessedRef = useRef(false); // Guard against duplicate verification handlers

  // 📱 Phone verification states
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [confirmationResult, setConfirmationResult] = useState(null);
  // Loading flag for the "Send SMS Code" button. Covers both Phase E
  // validatePhoneForSignup and the Firebase Phone Auth SMS round-trip,
  // so the user sees a single "Sending…" indicator for the whole flow.
  const [sendingCode, setSendingCode] = useState(false);

  // 👤 Profile information states
  const [username, setUsername] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null); // object URL for the selected-photo thumbnail
  const [usernameTaken, setUsernameTaken] = useState(false);
  const [usernameAvailability, setUsernameAvailability] = useState(''); // 'checking', 'available', 'taken', or ''

  // Validation constants for profile photo uploads. These mirror the
  // protections in AddPedalForm.jsx — anything looser leaves the door open
  // for the kind of multi-MB profile photos the audit found (some users had
  // 19MB+ profile photos that broke in Chrome).
  const PROFILE_PHOTO_MAX_UPLOAD_SIZE_MB = 15;
  const PROFILE_PHOTO_COMPRESSION_FALLBACK_MAX_MB = 10;
  const PROFILE_PHOTO_ACCEPTED_TYPES = [
    'image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif',
    'application/octet-stream' // iOS sometimes labels HEIC files this way
  ];

  const handlePhotoSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const sizeMB = file.size / 1024 / 1024;
    if (sizeMB > PROFILE_PHOTO_MAX_UPLOAD_SIZE_MB) {
      setError(
        `That photo is ${sizeMB.toFixed(1)}MB — too large to upload. ` +
        `Please choose a photo under ${PROFILE_PHOTO_MAX_UPLOAD_SIZE_MB}MB ` +
        `(most phones will compress to this if you reduce the resolution).`
      );
      e.target.value = ''; // clear input so the user can pick again
      return;
    }

    const typeOk = PROFILE_PHOTO_ACCEPTED_TYPES.includes(file.type) ||
      (file.type && file.type.startsWith('image/'));
    if (!typeOk) {
      setError(
        `Unsupported file type: ${file.type || 'unknown'}. ` +
        `Please use a JPG, PNG, or HEIC photo.`
      );
      e.target.value = '';
      return;
    }

    setError(''); // clear any previous validation error
    setPhoto(file);
    setPhotoPreview(URL.createObjectURL(file));
  };

  // Revoke the previous object URL whenever the preview changes or on unmount
  // so we don't leak blob URLs across re-selections.
  useEffect(() => {
    if (!photoPreview) return undefined;
    return () => URL.revokeObjectURL(photoPreview);
  }, [photoPreview]);

  // 📍 Shipping address states
  const [shippingAddress, setShippingAddress] = useState({
    name: '',
    street1: '',
    street2: '',
    city: '',
    state: '',
    zip: '',
    country: 'United States'
  });
  const [addressErrors, setAddressErrors] = useState({});
  const [placesApiLoaded, setPlacesApiLoaded] = useState(false);
  const [placesApiError, setPlacesApiError] = useState(false);

  const [checkingVerification, setCheckingVerification] = useState(false);

  // 🎁 Referral code states
  const [referralCode, setReferralCode] = useState('');
  const [referralStatus, setReferralStatus] = useState(''); // '', 'checking', 'valid', 'invalid'
  const [referredByUserId, setReferredByUserId] = useState('');
  const [searchParams] = useSearchParams();

  // Auto-populate AND auto-validate referral code from URL param (e.g., /signup?ref=CODE)
  // Persist to localStorage (shared across tabs) so it survives email verification tab switches
  const [referralFromUrl, setReferralFromUrl] = useState(false);
  useEffect(() => {
    const refParam = searchParams.get('ref');
    if (refParam && !referralCode) {
      const code = refParam.toUpperCase();
      setReferralCode(code);
      setReferralFromUrl(true);
      localStorage.setItem('pendingReferralCode', code);
    } else if (!referralCode) {
      // Recover from localStorage if state was lost (e.g., new tab from email verification, page refresh)
      const savedCode = localStorage.getItem('pendingReferralCode');
      if (savedCode) {
        console.log('🔗 Recovered referral code from localStorage:', savedCode);
        setReferralCode(savedCode);
        setReferralFromUrl(true);
      }
    }
  }, [searchParams]);

  // Auto-validate referral code when it was populated from URL param
  useEffect(() => {
    if (referralFromUrl && referralCode && referralStatus === '') {
      handleValidateReferralCode();
    }
  }, [referralFromUrl, referralCode]);

  // Helper: capitalize each word (e.g. "new york" → "New York", "colorado" → "Colorado")
  const capitalizeWords = (str) => {
    return str.replace(/\b\w/g, (char) => char.toUpperCase());
  };
  
  // 👁️ Preview mode for testing
  const [previewMode, setPreviewMode] = useState(false);
  
  // Check for preview mode on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('preview') === 'true') {
      setPreviewMode(true);
      setIsSignUp(true);
    }
  }, []);

  // Load Google Places API
  const loadGoogleMapsScript = () => {
    if (window.google && window.google.maps) {
      setPlacesApiLoaded(true);
      return;
    }

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${getGoogleMapsApiKey()}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      setPlacesApiLoaded(true);
    };
    script.onerror = () => {
      setPlacesApiError(true);
    };
    document.head.appendChild(script);
  };

  // Load Google Places API on component mount
  useEffect(() => {
    loadGoogleMapsScript();
  }, []);

  // Real-time username availability checking with debouncing
  useEffect(() => {
    if (!username || username.length < 3 || username.length > 20 || currentStep !== 4) {
      setUsernameAvailability('');
      return;
    }

    setUsernameAvailability('checking');
    
    const timer = setTimeout(async () => {
      // Local blocklist short-circuit (no network round-trip needed).
      if (isUsernameBlocklisted(username)) {
        setUsernameAvailability('taken');
        setUsernameTaken(true);
        return;
      }

      try {
        // Direct client read of the PUBLIC publicProfiles mirror. The client
        // can't read /users (rules lock it to self/admin), which is why a
        // naive client check missed legacy usernames — but publicProfiles is
        // `allow read: if true`, carries username + usernameLowercase for
        // every user (backfilled, and self-healing on each mirror write), and
        // is fast: a direct Firestore read with no Cloud Function cold start.
        const raw = username.trim();
        const normalized = raw.toLowerCase();
        const me = auth.currentUser?.uid;

        // The three lookups are independent, so run them IN PARALLEL. They
        // used to run sequentially, and an AVAILABLE name — the common case —
        // paid for all three round trips in series, which read as a
        // multi-second "checking…" on a phone connection (found during
        // Android testing, 2026-08-20).
        const titleCase = raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
        const variants = Array.from(new Set([raw, normalized, raw.toUpperCase(), titleCase]));
        const [reserved, lcSnap, legacySnap] = await Promise.all([
          // 1) Reserved name (usernames collection is public-readable, O(1)).
          getDoc(doc(db, 'usernames', normalized)),
          // 2) Modern users: indexed match on the normalized field.
          getDocs(query(collection(db, 'publicProfiles'), where('usernameLowercase', '==', normalized))),
          // 3) Legacy users (no usernameLowercase): Firestore can't query
          //    case-insensitively, so match common casings of the original
          //    `username` field (as typed / lower / UPPER / Title). Catches
          //    the owner account, whose username is stored lowercase.
          getDocs(query(collection(db, 'publicProfiles'), where('username', 'in', variants))),
        ]);

        if (reserved.exists() && reserved.data()?.userId !== me) {
          setUsernameAvailability('taken');
          setUsernameTaken(true);
          return;
        }

        if (lcSnap.docs.some((d) => d.id !== me)) {
          setUsernameAvailability('taken');
          setUsernameTaken(true);
          return;
        }

        if (legacySnap.docs.some((d) => d.id !== me)) {
          setUsernameAvailability('taken');
          setUsernameTaken(true);
          return;
        }

        setUsernameAvailability('available');
        setUsernameTaken(false);
      } catch (error) {
        console.error('Error checking username:', error);
        // Never fall through to a false "available" on error — clear to a
        // neutral state so the user can retry rather than proceed blindly.
        setUsernameAvailability('');
      }
    }, 500); // 500ms debounce

    return () => clearTimeout(timer);
  }, [username, currentStep]);

  // Check for existing verified user on component mount and recover state
  useEffect(() => {
    const checkInitialState = async () => {
      // Check URL path directly to avoid race condition with isSignUp prop
      const isOnSignupPage = window.location.pathname.includes('/signup');
      
      if (!isOnSignupPage) {
        console.log('🔍 Skipping recovery check: not on signup page');
        return;
      }

      // Wait for auth to be ready
      let user = auth.currentUser;
      if (!user) {
        console.log('🔍 Waiting for auth to load...');
        // Wait up to 2 seconds for user to load
        for (let i = 0; i < 20; i++) {
          await new Promise(resolve => setTimeout(resolve, 100));
          user = auth.currentUser;
          if (user) {
            console.log('🔍 Auth loaded, proceeding with recovery check');
            break;
          }
        }
      }
      
      if (!user) {
        console.log('🔍 Skipping recovery check: no user found after waiting');
        return;
      }

      try {
        await user.reload();
        
        // Check Firestore for user's actual verification status
        const userDocRef = doc(db, 'users', user.uid);
        const userDoc = await getDoc(userDocRef);
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          
          // Check account age to determine if we should trust Firebase Auth's emailVerified
          const accountCreatedAt = userData.createdAt?.toDate?.() || new Date();
          const accountAgeMinutes = (Date.now() - accountCreatedAt.getTime()) / (1000 * 60);
          
          // CRITICAL FIX: For NEW accounts (< 30 minutes old), ONLY trust Firestore
          // This prevents trusting cached emailVerified from deleted accounts
          // For OLDER accounts (likely returning users), trust Firebase Auth if it says verified
          const isEmailVerified = accountAgeMinutes < 30
            ? (userData.emailVerified === true)  // New account: only trust Firestore
            : (user.emailVerified === true || userData.emailVerified === true);  // Old account: trust both
          
          const isPhoneVerified = userData.phoneVerified;
          const hasCompleteProfile = userData.profileComplete === true || (userData.username && userData.shippingAddress?.street1);
          
          // Recover pending referral code from Firestore (persists across tabs/domains)
          if (userData.pendingReferralCode && !referralCode) {
            console.log('🔗 Recovered referral code from Firestore:', userData.pendingReferralCode);
            setReferralCode(userData.pendingReferralCode);
            setReferralFromUrl(true); // Trigger auto-validation
          }

          console.log('🔄 Recovering signup state:', {
            accountAgeMinutes: accountAgeMinutes.toFixed(1),
            firestoreEmailVerified: userData.emailVerified,
            authEmailVerified: user.emailVerified,
            finalEmailVerified: isEmailVerified,
            emailVerified: isEmailVerified,
            phoneVerified: isPhoneVerified,
            hasCompleteProfile,
            pendingReferralCode: userData.pendingReferralCode || null,
            fullUserData: userData
          });
          
          // Restore user to the correct step based on their actual verification status
          const isProfileComplete = userData.profileComplete || hasCompleteProfile;
          
          if (isProfileComplete) {
            // Already completed - redirect to dashboard
            console.log('✅ Profile already complete, redirecting to dashboard');
            navigate('/dashboard');
          } else if (isPhoneVerified && isEmailVerified) {
            // Phone verified, need profile
            console.log('✅ Email and phone verified, advancing to step 4 (profile)');
            setEmailVerified(true);
            setPhoneVerified(true);
            setCurrentStep(4);
          } else if (isEmailVerified) {
            // Email verified, need phone
            console.log('✅ Email verified, advancing to step 3 (phone)');
            setEmailVerified(true);
            setCurrentStep(3);
          } else {
            // Email NOT verified - stay on step 2 (email verification waiting)
            console.log('📧 Email not verified yet, staying on step 2');
            setCurrentStep(2);
          }
        } else if (user.emailVerified) {
          // DISABLED: Don't trust Firebase Auth cache for accounts without Firestore doc
          // This prevents advancing based on cached verification from deleted accounts
          console.log('⚠️ Email verified in Auth but no Firestore doc - likely cache issue, staying on step 2');
          setCurrentStep(2);
          /* DISABLED:
          // Email verified but no Firestore doc yet
          console.log('✅ Email verified, advancing to step 3 (phone)');
          setEmailVerified(true);
          setCurrentStep(3);
          */
        } else {
          // User exists but no Firestore doc and email not verified - step 2
          console.log('📧 Account exists but email not verified, staying on step 2');
          setCurrentStep(2);
        }
      } catch (error) {
        console.error('Error checking initial state:', error);
      }
    };

    // Run immediately without delay
    checkInitialState();
  }, [navigate]);



  // Pre-populate shipping address name with first and last name
  useEffect(() => {
    const fullName = `${firstName} ${lastName}`.trim();
    if (fullName && !shippingAddress.name) {
      setShippingAddress(prev => ({ ...prev, name: fullName }));
    }
  }, [firstName, lastName, shippingAddress.name]);

  // Single, robust email verification detection system
  useEffect(() => {
    const detectEmailVerification = async () => {
      // Check URL parameters first (user returning from email verification link)
      const urlParams = new URLSearchParams(window.location.search);
      const emailVerifiedParam = urlParams.get('emailVerified');

      // CRITICAL: If emailVerifiedParam is true but no user yet, WAIT for auth to load
      if (emailVerifiedParam === 'true') {
        let user = auth.currentUser;
        
        if (!user) {
          console.log('⏳ Email verification link detected, waiting for auth to load...');
          // Wait up to 3 seconds for auth to load
          for (let i = 0; i < 30; i++) {
            await new Promise(resolve => setTimeout(resolve, 100));
            user = auth.currentUser;
            if (user) {
              console.log('✅ Auth loaded, processing email verification');
              break;
            }
          }
        }

        if (!user) {
          console.log('❌ Auth did not load in time, email verification cannot be processed');
          // User clicked Continue from email verification page, but not logged in this window
          // Show a helpful message and close this window (it should have been opened by the original tab)
          if (window.opener) {
            // This is a popup/new tab, close it and notify parent
            window.opener.postMessage({ type: 'EMAIL_VERIFIED' }, window.location.origin);
            alert('Email verified! You can close this tab and return to the original signup page.');
            setTimeout(() => window.close(), 1000);
          } else {
            // This tab opened from the email link but doesn't have the auth session
            // The original signup tab should auto-advance via its Firestore listener
            alert('Email verified successfully! Please close this tab and return to your original signup tab to continue.');
          }
          return;
        }
        
        console.log('🔍 User returned from email verification link');
        
        try {
          // CRITICAL: Reload user from Firebase to get latest verification status
          await user.reload();
          console.log('🔍 Firebase Auth emailVerified status:', user.emailVerified);
          
          // ONLY advance if Firebase Auth confirms the email is actually verified
          if (user.emailVerified) {
            console.log('✅ Firebase Auth confirms email is verified');
            emailVerificationProcessedRef.current = true; // Prevent duplicate handlers
            
            // Update Firestore to match
            const userDocRef = doc(db, 'users', user.uid);
            await setDoc(userDocRef, {
              emailVerified: true,
              emailVerifiedAt: serverTimestamp()
            }, { merge: true });
            console.log('✅ Updated Firestore with emailVerified: true');
            
            // Clear URL params and advance to phone verification
            window.history.replaceState({}, document.title, '/signup');
            setEmailVerified(true);
            setCurrentStep(3);
            return;
          } else {
            console.warn('⚠️ URL says verified but Firebase Auth says NOT verified - ignoring');
            // Clear the misleading URL param
            window.history.replaceState({}, document.title, '/signup');
          }
        } catch (error) {
          console.error('❌ Error checking email verification:', error);
        }
        return; // Exit after processing emailVerifiedParam
      }
      
      const user = auth.currentUser;
      
      console.log('🔍 Email verification detection running:', {
        hasUser: !!user,
        isSignUp,
        currentStep,
        emailVerified,
        userEmailVerified: user?.emailVerified,
        urlSearch: window.location.search
      });
      
      const mode = urlParams.get('mode');
      const oobCode = urlParams.get('oobCode');

      // If Firebase handled email verification directly, update Firestore
      if (mode === 'verifyEmail' && oobCode && user) {
        console.log('🔍 Detected Firebase email verification completion');
        
        try {
          // Firebase already verified the email, now update Firestore
          const userDocRef = doc(db, 'users', user.uid);
          await setDoc(userDocRef, {
            emailVerified: true,
            emailVerifiedAt: serverTimestamp()
          }, { merge: true });
          console.log('✅ Updated Firestore with emailVerified: true');
          
          // Clear URL params and advance
          window.history.replaceState({}, document.title, '/signup');
          setEmailVerified(true);
          setCurrentStep(3);
          return;
        } catch (error) {
          console.error('Error updating Firestore after verification:', error);
        }
      }
      
      // Check URL parameters first (user returning from email verification link)
      const verified = urlParams.get('verified');
      const emailParam = urlParams.get('email');

      // If verified=true in URL but NO user, we're in a popup after email verification
      // Close this popup and tell the parent window to refresh
      if (verified === 'true' && !user && window.opener) {
        console.log('✅ Email verified in popup, closing and refreshing parent window');
        try {
          window.opener.postMessage({ type: 'EMAIL_VERIFIED' }, window.location.origin);
          setTimeout(() => window.close(), 500);
        } catch (e) {
          console.warn('Could not close popup:', e);
        }
        return;
      }
      
      // Only proceed if we have a user and we're in signup mode
      if (!user || !isSignUp) {
        console.log('🔍 Skipping detection - no user or not in signup mode');
        return;
      }

      try {
        // Check URL parameters first (user returning from email verification link)
        const urlParams = new URLSearchParams(window.location.search);
        const verified = urlParams.get('verified');
        const emailParam = urlParams.get('email');

        if (verified === 'true' && emailParam) {
          if (emailVerificationProcessedRef.current) {
            console.log('⏭️ Email verification already processed by another handler, skipping');
            window.history.replaceState({}, document.title, window.location.pathname);
            return;
          }

          console.log('🔍 Detected email verification completion (fallback handler)');
          
          // Reload user to get latest verification status
          await user.reload();
          console.log('🔍 After reload - user.emailVerified:', user.emailVerified);
          
          if (user.emailVerified) {
            console.log('✅ Email verification successful via link');
            emailVerificationProcessedRef.current = true; // Prevent duplicate handlers
            
            // CRITICAL: Update Firestore with emailVerified status
            const userDocRef = doc(db, 'users', user.uid);
            await setDoc(userDocRef, {
              emailVerified: true,
              emailVerifiedAt: serverTimestamp()
            }, { merge: true });
            console.log('✅ Updated Firestore with emailVerified: true');
            
            // Check user's full verification status to determine next step
            const userDoc = await getDoc(userDocRef);
            
            if (userDoc.exists()) {
              const userData = userDoc.data();
              const isPhoneVerified = userData.phoneVerified;
              const hasCompleteProfile = userData.profileComplete === true || (userData.username && userData.shippingAddress?.street1);
              
              setEmailVerified(true);
              setIsSignUp(true); // Ensure we're in signup mode
              
              if (!isPhoneVerified) {
                console.log('📱 Redirecting to phone verification');
                setCurrentStep(3);
              } else if (!hasCompleteProfile) {
                console.log('👤 Redirecting to profile completion');
                setPhoneVerified(true);
                setCurrentStep(4);
              } else {
                console.log('✅ All verification complete, redirecting to dashboard');
                navigate('/dashboard');
                return;
              }
            } else {
              // No Firestore doc yet, create it with emailVerified
              await setDoc(userDocRef, {
                email: user.email,
                emailVerified: true,
                emailVerifiedAt: serverTimestamp(),
                phoneVerified: false,
                createdAt: serverTimestamp()
              }, { merge: true });
              
              setEmailVerified(true);
              setCurrentStep(3);
              setIsSignUp(true);
            }
            
            // Clear URL parameters
            const newUrl = window.location.pathname;
            window.history.replaceState({}, document.title, newUrl);
            
            // If this is a fresh window (no opener), try to close it
            if (!window.opener) {
              try {
                window.close();
              } catch (e) {
                // Can't close - user will see step 3 in this tab
              }
            }
            return;
          } else {
            console.log('❌ Email verification status not updated yet, will retry');
            // Don't show error, let the polling mechanism handle it
            return;
          }
        }

        // DISABLED: This auto-advances based on Firebase Auth cache without actual link click
        // Only allow verification through URL parameter (user clicking email link)
        // Check if user is already verified but we haven't updated state yet
        if (false && user.emailVerified && !emailVerified) {
          console.log('⚠️ DISABLED: Auto-advance based on user.emailVerified cache');
          
          /* DISABLED CODE:
          // Update Firestore
          const userDocRef = doc(db, 'users', user.uid);
          await setDoc(userDocRef, {
            emailVerified: true,
            emailVerifiedAt: serverTimestamp()
          }, { merge: true });
          
          setEmailVerified(true);
          setCurrentStep(3);
          return;
          */
        }

        // FIXED VERSION - Only correct step 1 to step 3, NEVER touch step 4 [TIMESTAMP: 2025-08-09 01:56]
        if (user.emailVerified && emailVerified && currentStep === 1) {
          console.log('🔧 CACHE BUSTED: User is email verified but on step 1, correcting to step 3');
          setCurrentStep(3);
          return;
        }

      } catch (error) {
        console.error('Error in email verification detection:', error);
      }
    };

    detectEmailVerification();
  }, [window.location.search, isSignUp, currentStep, emailVerified]);

  // Listen for email verification messages from popup windows
  useEffect(() => {
    const handleMessage = async (event) => {
      // Security: only accept messages from our own origin
      if (event.origin !== window.location.origin) return;
      
      if (event.data?.type === 'EMAIL_VERIFIED') {
        if (emailVerificationProcessedRef.current) {
          console.log('⏭️ postMessage handler: verification already processed, skipping');
          return;
        }
        console.log('✅ Received email verification confirmation from popup, reloading user...');
        const user = auth.currentUser;
        if (user) {
          try {
            await user.reload();
            const userDocRef = doc(db, 'users', user.uid);
            const userDoc = await getDoc(userDocRef);
            
            if (userDoc.exists()) {
              const userData = userDoc.data();
              // Check if Firestore was updated by the verification
              if (userData.emailVerified) {
                emailVerificationProcessedRef.current = true;
                setEmailVerified(true);
                setCurrentStep(3);
              } else {
                // Firestore not updated yet, refresh page to trigger recovery logic
                window.location.reload();
              }
            }
          } catch (error) {
            console.error('Error reloading after email verification:', error);
            // Fallback: just reload the page
            window.location.reload();
          }
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  // Listen for Firestore changes to emailVerified status (auto-advance when email is verified)
  useEffect(() => {
    // Only listen during signup on step 1 or 2 (when email verification is pending)
    if (!isSignUp || (currentStep !== 1 && currentStep !== 2)) return;
    
    const user = auth.currentUser;
    if (!user) return;

    console.log('👂 Setting up Firestore listener for email verification...');
    
    const userDocRef = doc(db, 'users', user.uid);
    const unsubscribe = onSnapshot(userDocRef, (snapshot) => {
      if (snapshot.exists()) {
        const userData = snapshot.data();
        console.log('📬 Firestore update detected - emailVerified:', userData.emailVerified);
        
        if (userData.emailVerified && !emailVerified) {
          if (emailVerificationProcessedRef.current) {
            console.log('⏭️ Firestore listener: verification already processed, just updating state');
          } else {
            console.log('✅ Email verified detected via Firestore listener, advancing to step 3');
            emailVerificationProcessedRef.current = true;
          }
          setEmailVerified(true);
          setCurrentStep(3);
        }
      }
    }, (error) => {
      console.error('Error listening to Firestore:', error);
    });

    return () => unsubscribe();
  }, [isSignUp, currentStep, emailVerified]);

  // CRITICAL: Poll Firebase Auth to detect email verification (backup to Firestore listener)
  // This catches the case where user clicks verify link but Continue opens in logged-out window
  useEffect(() => {
    if (!isSignUp || currentStep !== 2 || emailVerified) return;
    
    const user = auth.currentUser;
    if (!user) return;

    console.log('🔄 Starting Firebase Auth polling for email verification...');
    
    const pollInterval = setInterval(async () => {
      try {
        await user.reload();
        
        if (user.emailVerified) {
          console.log('✅ Email verification detected via polling!');
          
          // Update Firestore (which will trigger the listener above)
          const userDocRef = doc(db, 'users', user.uid);
          await setDoc(userDocRef, {
            emailVerified: true,
            emailVerifiedAt: serverTimestamp()
          }, { merge: true });
          
          console.log('✅ Updated Firestore via polling');
        }
      } catch (error) {
        console.error('Error polling for email verification:', error);
      }
    }, 3000); // Check every 3 seconds

    return () => {
      console.log('🛑 Stopping email verification polling');
      clearInterval(pollInterval);
    };
  }, [isSignUp, currentStep, emailVerified]);

  // Native: when the user returns from verifying email in an external browser
  // (via the /email-verified deep link, or by switching back manually), re-check
  // verification immediately instead of waiting for the next 3s poll tick. The
  // existing Firestore onSnapshot listener handles advancing to step 3.
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    if (!isSignUp || currentStep !== 2 || emailVerified) return;

    let cleanup = () => {};
    (async () => {
      const { App } = await import('@capacitor/app');

      const recheck = async () => {
        const user = auth.currentUser;
        if (!user) return;
        // Visible feedback while the reload → Firestore write → snapshot chain
        // runs: without it the user sits on the resend page for several seconds
        // after returning from their email app, wondering if the tap registered
        // (found during Android testing, 2026-08-20).
        setCheckingVerification(true);
        try {
          await user.reload();
          if (user.emailVerified) {
            await setDoc(doc(db, 'users', user.uid), {
              emailVerified: true,
              emailVerifiedAt: serverTimestamp()
            }, { merge: true });
          }
        } catch (err) {
          console.error('Error re-checking email verification on resume:', err);
        } finally {
          setCheckingVerification(false);
        }
      };

      const resumeHandle = await App.addListener('resume', recheck);
      const urlHandle = await App.addListener('appUrlOpen', recheck);
      cleanup = () => {
        resumeHandle.remove();
        urlHandle.remove();
      };
    })();

    return () => cleanup();
  }, [isSignUp, currentStep, emailVerified]);

  // Additional check that runs more frequently
  useEffect(() => {
    const checkVerificationStatus = async () => {
      // TEMPORARILY DISABLED: Firebase Auth is caching "verified" status from deleted accounts
      // This causes security bypass where users can advance without clicking email verification
      // TODO: Re-enable after figuring out Firebase Auth caching issue
      return;
      
      /* DISABLED CODE - DO NOT USE UNTIL CACHE ISSUE RESOLVED
      const user = auth.currentUser;
      if (user && isSignUp && (currentStep === 2 || currentStep === 1)) {
        try {
          await user.reload();
          if (user.emailVerified && !emailVerified) {
            console.log('✅ Additional check: User is verified, advancing to step 3');
            
            // Update Firestore
            const userDocRef = doc(db, 'users', user.uid);
            await setDoc(userDocRef, {
              emailVerified: true,
              emailVerifiedAt: serverTimestamp()
            }, { merge: true });
            
            setEmailVerified(true);
            setCurrentStep(3);
            setIsSignUp(true); // Ensure we stay in signup mode
          }
        } catch (error) {
          console.error('Error in additional verification check:', error);
        }
      }
      */
    };

    // Check every 1 second when on step 1 or 2 (more aggressive)
    // DISABLED with the function above
    if (false && (currentStep === 1 || currentStep === 2)) {
      const interval = setInterval(checkVerificationStatus, 1000);
      return () => clearInterval(interval);
    }
  }, [currentStep, isSignUp, emailVerified]);

  const checkUsernameExists = async (uname) => {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        return false;
      }
      
      // Use efficient query on usernames collection (case-insensitive by storing lowercase)
      const normalizedUsername = uname.toLowerCase().trim();
      const usernameDoc = await getDoc(doc(db, 'usernames', normalizedUsername));
      
      return usernameDoc.exists();
    } catch (error) {
      console.error('Error checking username existence:', error);
      // On error, fail safe by assuming username might be taken
      return true;
    }
  };

  // Step 1: Email and password only
  const handleEmailPasswordSubmit = async (e) => {
    e.preventDefault();
    if (previewMode) {
      setCurrentStep(2);
      return;
    }
    setError('');

    if (!email || !password) {
      setError('Please enter both email and password.');
      return;
    }

    setIsCreatingAccount(true);

    // Phase E: Bot-defense preflight (AbstractAPI IP + email). Runs BEFORE
    // createUserWithEmailAndPassword so we don't create orphan Firebase Auth
    // users for bot signups. Fail-open on AbstractAPI / infra failure — see
    // functions/preflightSignupCheck.js header.
    try {
      const fns = getFunctions();
      const preflightFn = httpsCallable(fns, 'preflightSignupCheck');
      await preflightFn({ email });
    } catch (preflightErr) {
      const code = preflightErr?.code || '';
      const msg = preflightErr?.message || '';
      console.warn('preflightSignupCheck result:', code, msg);

      // Phase D Pass 3 — banned-identifier match. MUST come before the
      // generic permission-denied branch below (also `permission-denied`),
      // otherwise that catch swallows this case and shows the wrong
      // "network" message instead of the banned-account message.
      if (
        code === 'functions/permission-denied' &&
        msg.includes('BANNED_IDENTIFIER_EMAIL')
      ) {
        setIsCreatingAccount(false);
        setError(
          'This account cannot be created. Your details match a banned account on PedalxChange. If you believe this is an error, contact contact@pedalxchange.com.'
        );
        return;
      }
      if (code === 'functions/permission-denied') {
        setIsCreatingAccount(false);
        setError(
          'Sign-up is not available from your current network. Please try again from a different connection, or contact contact@pedalxchange.com if you need help.'
        );
        return;
      }
      if (code === 'functions/resource-exhausted') {
        setIsCreatingAccount(false);
        setError(
          'Too many sign-up attempts from this network. Please wait a few minutes and try again.'
        );
        return;
      }
      if (
        code === 'functions/failed-precondition' ||
        code === 'functions/invalid-argument'
      ) {
        setIsCreatingAccount(false);
        setError(
          'Please use a valid established email address to sign up. Contact contact@pedalxchange.com if you believe this is a mistake.'
        );
        return;
      }
      // Phase D Pass 3 — Firestore source-read failure (fail-closed).
      // MUST return; do NOT fall through to the fail-open path below,
      // because that would let banned users sneak past during Firestore
      // outages (the entire point of fail-closed on this gate).
      if (code === 'functions/unavailable') {
        setIsCreatingAccount(false);
        setError(
          'Sign-up is temporarily unavailable. Please try again in a few moments.'
        );
        return;
      }
      // Unknown error (e.g. network to callable, function not deployed) —
      // fail-open per the "never block legit users on infra failure" rule.
      // Server-side errorLogs already captured the underlying cause.
      console.warn(
        'preflightSignupCheck unknown error — failing open and proceeding to createUser'
      );
    }

    try {
      // Create user with email and password only
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      console.log('✅ User created successfully:', user.uid);
      console.log('🔍 IMMEDIATELY AFTER CREATION - user.emailVerified:', user.emailVerified);
      console.log('🔍 IMMEDIATELY AFTER CREATION - user.metadata:', user.metadata);

      // IMMEDIATELY create basic Firestore document so phone verification can update it
      // Also save any pending referral code so it persists across tabs/domains
      const initialUserData = {
        email: user.email,
        createdAt: serverTimestamp(),
        emailVerified: false,
        phoneVerified: false,
        accountStatus: 'active'
      };
      
      // Check for referral code from state or localStorage
      const pendingRef = referralCode || localStorage.getItem('pendingReferralCode');
      if (pendingRef) {
        initialUserData.pendingReferralCode = pendingRef.toUpperCase();
        console.log('🔗 Saving pending referral code to Firestore:', pendingRef);
      }
      
      await setDoc(doc(db, 'users', user.uid), initialUserData, { merge: true });
      console.log('✅ Basic user document created in Firestore');
      console.log('🔍 AFTER FIRESTORE CREATION - user.emailVerified:', user.emailVerified);

      // Fire-and-forget the verification email send. The UI advances to
      // step 2 the moment Firebase Auth + Firestore are ready, while the
      // email goes out in parallel. Fallback chain (Resend → Firebase
      // default) and error handling are identical to the previous awaited
      // version; failures are logged and the user can retry from the
      // "Resend Verification Email" button on step 2.
      (async () => {
        try {
          const functions = getFunctions();
          const sendCustomEmailVerification = httpsCallable(functions, 'sendCustomEmailVerification');
          const continueUrl = getEmailContinueUrl();

          await sendCustomEmailVerification({ email, continueUrl });
          console.log('✅ Custom verification email sent successfully');
        } catch (customEmailError) {
          console.warn('⚠️ Custom email verification failed, falling back to Firebase default:', customEmailError);
          try {
            const actionCodeSettings = {
              url: `${getEmailLinkOrigin()}/signup?verified=true&email=${encodeURIComponent(email)}`,
              handleCodeInApp: true
            };
            await sendEmailVerification(user, actionCodeSettings);
            console.log('✅ Fallback verification email sent successfully');
          } catch (fallbackError) {
            console.error('❌ Both custom and fallback email sending failed:', fallbackError);
          }
        }
      })();
      
      // Prompt browser to save credentials (works in Chromium-based browsers like Brave/Chrome)
      try {
        if (window.PasswordCredential) {
          const cred = new window.PasswordCredential({ id: email, password: password });
          await navigator.credentials.store(cred);
          console.log('✅ Browser prompted to save credentials');
        }
      } catch (credErr) {
        console.log('ℹ️ Credential storage not available:', credErr.message);
      }

      // Always advance to next step, even if email sending failed
      setIsCreatingAccount(false);
      setEmailSent(true);
      setCurrentStep(2);
      // No alert — Step 2 already tells the user to check their email

  } catch (err) {
    setIsCreatingAccount(false);
    console.error('Auth error:', err.message);
    
    // Handle case where email is already in use - attempt to continue signup flow
    if (err.code === 'auth/email-already-in-use') {
      try {
        console.log('🔄 Email already in use, attempting to sign in and continue verification...');
        
        // Try to sign in with the existing account
        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        const user = userCredential.user;
        
        console.log('✅ Signed in existing user:', user.uid);
        
        // Check user's verification status
        const userDocRef = doc(db, 'users', user.uid);
        const userDoc = await getDoc(userDocRef);
        
        // CRITICAL CHECK: If Firestore document doesn't exist but Auth account does,
        // this is an ORPHANED account from incomplete deletion
        if (!userDoc.exists()) {
          console.warn('⚠️ ORPHANED ACCOUNT DETECTED: Auth exists but Firestore does not');
          
          // SAFETY CHECKS before auto-deletion:
          // 1. Check if account is very new (< 5 minutes) - might be mid-signup
          const accountAgeMs = Date.now() - new Date(user.metadata.creationTime).getTime();
          const accountAgeMinutes = accountAgeMs / (1000 * 60);
          
          if (accountAgeMinutes < 5) {
            console.log('⚠️ Account is very new (< 5 min), this might be a normal signup in progress');
            console.log('   Creating minimal Firestore document to allow signup to continue...');
            
            // Create minimal document to allow signup to continue
            await setDoc(doc(db, 'users', user.uid), {
              email: user.email,
              createdAt: serverTimestamp(),
              emailVerified: user.emailVerified || false,
              phoneVerified: false,
              accountStatus: 'active'
            }, { merge: true });
            
            // Continue with normal flow
            if (!user.emailVerified) {
              setCurrentStep(2);
              setEmailSent(true);
              setError('Please check your email (including spam folder) and click the verification link to continue.');
            } else {
              setEmailVerified(true);
              setCurrentStep(3);
              setError('Email verified! Please continue with phone verification.');
            }
            return;
          }
          
          // 2. Account is old enough to be considered orphaned
          console.log(`🔄 Account age: ${accountAgeMinutes.toFixed(1)} minutes - proceeding with orphan cleanup...`);
          
          // Try to delete the orphaned Auth account
          try {
            // Call deleteUserAccount cloud function to properly clean up
            const functions = getFunctions();
            const deleteAccount = httpsCallable(functions, 'deleteUserAccount');
            await deleteAccount({ deletionReason: 'Orphaned account cleanup during re-registration' });
            
            console.log('✅ Orphaned account deleted, please try signing up again');
            setError('Required account clean up performed. Please navigate sign up flow once again.');
            
            // Sign out the orphaned account
            await auth.signOut();
            return;
          } catch (deleteError) {
            console.error('❌ Failed to delete orphaned account:', deleteError);
            setError('Account exists but is in an invalid state. Please contact support with error code: ORPHANED_ACCOUNT');
            await auth.signOut();
            return;
          }
        }
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          
          // Check if account is banned. ('suspended' status was removed
          // 2026-04-28 — it was a dead legacy state never written by any
          // current code path.)
          if (userData.accountStatus === 'banned') {
            await auth.signOut();
            setError('This account has been banned or disabled. Please contact us at contact@pedalxchange.com');
            return;
          }

          // Auto-suspended pending admin review (Phase D-lite). Generic
          // wording on purpose: do not name the matched signal, otherwise
          // bypasses become easy to iterate.
          if (userData.accountStatus === 'suspended_pending_review') {
            await auth.signOut();
            setError('Your account has been suspended pending review. If you believe this is a mistake, please contact contact@pedalxchange.com.');
            return;
          }
          
          // Check where they left off in the verification process
          // Check BOTH Firebase Auth and Firestore for email verification (Firestore is more reliable)
          const isEmailVerified = user.emailVerified || userData.emailVerified;
          const isPhoneVerified = userData.phoneVerified;
          const isFullyVerified = userData.isVerified;
          const hasCompleteProfile = userData.profileComplete === true || (userData.username && userData.shippingAddress?.street1);
          
          if (isFullyVerified && isEmailVerified && isPhoneVerified && hasCompleteProfile) {
            // Account is fully set up - redirect to dashboard
            console.log('✅ Account fully verified, redirecting to dashboard');
            navigate('/dashboard');
            return;
          }
          
          // Continue signup flow from where they left off
          if (!isEmailVerified) {
            console.log('📧 Email not verified, continuing from email verification step');
            setCurrentStep(2);
            setEmailSent(true);
            setError('Please check your email (including spam folder) and click the verification link to continue.');
          } else if (!isPhoneVerified) {
            console.log('📱 Phone not verified, continuing from phone verification step');
            setEmailVerified(true);
            setCurrentStep(3);
            setError('Email verified! Please continue with phone verification.');
          } else if (!hasCompleteProfile || !userData.username) {
            console.log('👤 Profile incomplete, continuing from profile completion step');
            setEmailVerified(true);
            setPhoneVerified(true);
            setCurrentStep(4);
            setError('Please complete your profile information.');
          }
        } else {
          // User exists in auth but not in Firestore - create basic document and continue
          console.log('📝 Creating missing user document...');
          await setDoc(doc(db, 'users', user.uid), {
            email: user.email,
            createdAt: serverTimestamp(),
            emailVerified: false,
            phoneVerified: false,
            accountStatus: 'active'
          }, { merge: true });
          
          if (!user.emailVerified) {
            setCurrentStep(2);
            setEmailSent(true);
            setError('Please check your email (including spam folder) and click the verification link to continue.');
          } else {
            setEmailVerified(true);
            setCurrentStep(3);
            setError('Email verified! Please continue with phone verification.');
          }
        }
        
      } catch (signInError) {
        console.error('Sign in error:', signInError.message);
        if (signInError.code === 'auth/wrong-password' || signInError.code === 'auth/invalid-credential') {
          setError('An account with this email already exists. Please log in instead, or use "Forgot Password" on the login page to reset your password.');
        } else if (signInError.code === 'auth/network-request-failed') {
          setError('Network connection issue. Please check your internet and try again.');
        } else {
          setError('An account with this email already exists. Please log in instead, or contact us at contact@pedalxchange.com if you need help accessing it.');
        }
      }
    } else if (err.code === 'auth/weak-password') {
      setError('Password is too weak. Please use at least 6 characters.');
    } else if (err.code === 'auth/invalid-email') {
      setError('Invalid email address. Please check your email and try again.');
    } else if (err.code === 'auth/operation-not-allowed') {
      setError('Sign up is currently unavailable. Please contact us at contact@pedalxchange.com');
    } else if (err.code === 'auth/network-request-failed') {
      setError('Network connection issue. Please check your internet connection and try again.');
    } else if (err.code === 'auth/too-many-requests') {
      setError('Too many attempts. Please wait a few minutes and try again.');
    } else {
      setError('Sign up failed. Please contact us at contact@pedalxchange.com');
    }
  }
};

  // Step 2: Email verification confirmation - REMOVED - now handled automatically
  // const handleEmailVerificationCheck = async () => {
  //   setCheckingVerification(true);
  //   setError('');

  //   try {
  //     const user = auth.currentUser;
  //     if (!user) {
  //       setError('No active user found.');
  //       return;
  //     }

  //     await user.reload();
      
  //     if (user.emailVerified) {
  //       setEmailVerified(true);
  //       setCurrentStep(3);
  //       console.log('✅ Email verified, moving to phone verification');
  //     } else {
  //       setError('Email not verified yet. Please check your inbox and click the verification link.');
  //     }
  //   } catch (err) {
  //     console.error('Email verification check error:', err.message);
  //     setError(err.message);
  //   } finally {
  //     setCheckingVerification(false);
  //   }
  // };

  // Wire reCAPTCHA cleanup to step 3 lifecycle. Ensures
  // firebasePhoneAuthService.cleanup() runs before React removes the
  // recaptcha-container <div> from the DOM, so Google's reCAPTCHA registry
  // releases the widget registration before the container node disappears.
  // Fixes the "reCAPTCHA has already been rendered in this element" error
  // that occurs when a user leaves and returns to step 3.
  useEffect(() => {
    if (currentStep !== 3) return;
    return () => {
      firebasePhoneAuthService.cleanup();
    };
  }, [currentStep]);

  // Step 3: Phone verification
 const handleSendCode = async () => {
  setError('');
  if (sendingCode) return; // Guard against double-click while in flight.
  setSendingCode(true);
  console.log('🔍 Starting Firebase Phone Auth verification for phone:', phone);

  try {
    if (!phone) {
      setError('Please enter a phone number');
      return;
    }

    // Strip all non-digit characters first
    const digitsOnly = phone.replace(/\D/g, '');
    
    // Validate we have enough digits
    if (digitsOnly.length < 10) {
      setError('Please enter a valid 10-digit phone number');
      return;
    }

    // Format phone number for Firebase
    let formattedPhone;
    if (phone.startsWith('+')) {
      // If already has + prefix, verify it's US (+1)
      if (!phone.startsWith('+1')) {
        setError('Only US phone numbers are supported (+1)');
        return;
      }
      // Keep as-is but strip any non-digits after +1
      formattedPhone = `+1${digitsOnly.replace(/^1/, '')}`; // Remove leading 1 if present after stripping
    } else if (digitsOnly.length === 10) {
      // 10 digits: 9496982155 -> +19496982155
      formattedPhone = `+1${digitsOnly}`;
    } else if (digitsOnly.length === 11 && digitsOnly.startsWith('1')) {
      // 11 digits starting with 1: 19496982155 -> +19496982155
      formattedPhone = `+${digitsOnly}`;
    } else if (digitsOnly.length === 11) {
      // 11 digits not starting with 1: probably invalid, but try adding +1
      formattedPhone = `+1${digitsOnly}`;
    } else if (digitsOnly.length > 11) {
      // Too many digits, assume it might have country code
      // Take last 10 digits and add +1
      formattedPhone = `+1${digitsOnly.slice(-10)}`;
    } else {
      setError('Please enter a valid phone number');
      return;
    }
    
    console.log('🔍 Formatted phone number:', formattedPhone);

    // Phase E: Bot-defense — gate AbstractAPI Phone Intelligence BEFORE SMS.
    // Saves SMS cost on bot attempts and blocks VoIP / disposable / abuse
    // numbers. Fail-open on AbstractAPI / infra failure — see
    // functions/validatePhoneForSignup.js header.
    try {
      const fns = getFunctions();
      const validatePhoneFn = httpsCallable(fns, 'validatePhoneForSignup');
      await validatePhoneFn({ phoneNumber: formattedPhone });
    } catch (phoneErr) {
      const code = phoneErr?.code || '';
      const msg = phoneErr?.message || '';
      console.warn('validatePhoneForSignup result:', code, msg);

      // Phase D Pass 3 — banned-identifier match. The validatePhoneForSignup
      // callable doesn't otherwise throw permission-denied, but we still match
      // on the message to be defensive against future overlap and to mirror
      // the preflight branch ordering.
      if (
        code === 'functions/permission-denied' &&
        msg.includes('BANNED_IDENTIFIER_PHONE')
      ) {
        setError(
          'This account cannot be created. Your details match a banned account on PedalxChange. If you believe this is an error, contact contact@pedalxchange.com.'
        );
        return;
      }
      if (code === 'functions/unauthenticated') {
        setError(
          'You must be signed in to verify a phone number. Please refresh and try again.'
        );
        return;
      }
      if (code === 'functions/resource-exhausted') {
        setError(
          'Too many phone-number attempts. Please wait 24 hours and try again, or contact contact@pedalxchange.com if this is a mistake.'
        );
        return;
      }
      if (
        code === 'functions/failed-precondition' ||
        code === 'functions/invalid-argument'
      ) {
        setError(
          'Please use a real mobile number to verify your account. If you believe this is a mistake, contact contact@pedalxchange.com.'
        );
        return;
      }
      // Phase D Pass 3 — Firestore source-read failure (fail-closed).
      // MUST return; do NOT fall through to the fail-open path below.
      if (code === 'functions/unavailable') {
        setError(
          'Phone verification is temporarily unavailable. Please try again in a few moments.'
        );
        return;
      }
      // Unknown error — fail-open per the "never block legit users on infra
      // failure" rule. Server-side errorLogs already captured the cause.
      console.warn(
        'validatePhoneForSignup unknown error — failing open and proceeding to SMS'
      );
    }

    const result = await firebasePhoneAuthService.sendVerificationCode(formattedPhone, 'recaptcha-container');

    if (result.success) {
      console.log('✅ SMS sent via Firebase Phone Auth');
      
      setConfirmationResult({
        session: result.session,
        phoneNumber: formattedPhone,
        confirm: async (code) => {
          console.log('🔍 Verifying SMS code:', code);
          
                      const verifyResult = await firebasePhoneAuthService.verifyCode(result.session, code, formattedPhone);

          if (verifyResult.success) {
             console.log('✅ Phone verification successful');
             return { success: true };
          } else {
            throw new Error(verifyResult.message || 'Invalid verification code');
          }
        }
      });

      alert('SMS sent! Please check your messages for the verification code.');
    } else {
      throw new Error('Failed to send SMS');
    }
  } catch (err) {
    console.error('❌ Error during phone verification:', err);
    
    // Provide user-friendly error messages
    if (err.isFriendly) {
      // Already translated by the phone auth service — show verbatim.
      setError(err.message);
    } else if (err.message?.includes('TOO_SHORT')) {
      setError('Phone number is too short. Please enter a complete 10-digit US phone number. Note: PedalxChange is currently available to US residents only.');
    } else if (err.message?.includes('TOO_LONG')) {
      setError('Phone number is too long. Please enter only a 10-digit US phone number. Note: PedalxChange is currently available to US residents only.');
    } else if (err.message?.includes('INVALID_PHONE_NUMBER')) {
      setError('Invalid phone number format. Please enter a valid US phone number (e.g., 949-555-1234). Note: PedalxChange is currently available to US residents only.');
    } else if (err.message?.includes('quota')) {
      setError('SMS quota exceeded. Please contact support or try again later.');
    } else if (err.message?.includes('network')) {
      setError('Network connection issue. Please check your internet and try again.');
    } else if (err.code === 'auth/invalid-phone-number') {
      setError('Invalid phone number. Please enter a valid US phone number. Note: PedalxChange is currently available to US residents only.');
    } else if (err.code === 'auth/missing-phone-number') {
      setError('Please enter a phone number.');
    } else {
      setError('Phone verification failed: ' + (err.message || 'Unknown error') + '. Please contact support if this continues.');
    }
  } finally {
    // Always release the spinner — covers success, early returns inside
    // try, the Phase E validatePhoneForSignup catch's returns, and the
    // outer SMS-send catch above.
    setSendingCode(false);
  }
};

  const handleVerifyCode = async () => {
    try {
      const user = auth.currentUser;
      console.log('🔍 DEBUGGING SMS VERIFICATION:', {
        userExists: !!user,
        userId: user?.uid,
        userEmail: user?.email
      });
      
      // Check if user document exists before verification
      if (user) {
        const userDocRef = doc(db, 'users', user.uid);
        const userDoc = await getDoc(userDocRef);
        console.log('🔍 USER DOCUMENT BEFORE SMS VERIFICATION:', {
          documentExists: userDoc.exists(),
          documentData: userDoc.exists() ? userDoc.data() : null
        });
      }
      
      const result = await confirmationResult.confirm(code);
      console.log('✅ SMS verification result:', result);
      
      // CRITICAL: Update Firestore with phoneVerified status
      if (user) {
        const userDocRef = doc(db, 'users', user.uid);
        await setDoc(userDocRef, {
          phoneVerified: true,
          phoneVerifiedAt: serverTimestamp(),
          phoneNumber: phone // Use the phone variable from state, not result object
        }, { merge: true });
        console.log('✅ Updated Firestore with phoneVerified: true');
      }
      
      console.log('🔍 Setting phoneVerified to true...');
      setPhoneVerified(true);
      // DON'T auto-advance to step 4 - let user click Continue button instead
      
      console.log('🔍 Phone verified! User can now click Continue to add address...');
      
      alert('Phone number verified! Click "Continue" below to complete your profile.');

    } catch (error) {
      console.error('❌ Code verification failed:', error.message);
      console.error('❌ Full error object:', error);
      
      // The phone auth service already translated this into a user-friendly
      // message (e.g. "This phone number is already registered to another
      // PedalxChange account...") — show it verbatim.
      if (error.isFriendly) {
        setError(error.message);
      } else if (error.code === 'auth/invalid-verification-code') {
        setError('Invalid verification code. Please check the code and try again, or request a new code.');
      } else if (error.code === 'auth/code-expired') {
        setError('Verification code has expired. Please request a new code.');
      } else if (error.message?.includes('invalid') || error.message?.includes('Invalid')) {
        setError('Invalid verification code. Please enter the 6-digit code from your text message.');
      } else if (error.message?.includes('expired')) {
        setError('Verification code has expired. Please click "Send Code" to get a new one.');
      } else if (error.message?.includes('network')) {
        setError('Network connection issue. Please check your internet and try again.');
      } else {
        setError('We couldn\'t verify that code. Please try again, or contact us at contact@pedalxchange.com if this continues.');
      }
    }
  };

  // Validate referral code
  const handleValidateReferralCode = async () => {
    const code = referralCode.trim().toUpperCase();
    if (!code) {
      setReferralStatus('');
      return;
    }
    
    setReferralStatus('checking');
    
    try {
      // Server-side resolution. The client cannot query /users (Firestore
      // rules lock it to self/admin), so the old client-side query was
      // silently denied and the catch below always forced "invalid" — which
      // also meant referredBy was never set and the referrer never got
      // credited. The callable resolves the code with the Admin SDK and
      // handles the self-referral case. See functions/validateReferralCode.js.
      const fns = getFunctions();
      const validateFn = httpsCallable(fns, 'validateReferralCode');
      const { data } = await validateFn({ code });

      if (data?.valid && data.referrerId) {
        setReferralStatus('valid');
        setReferredByUserId(data.referrerId);
      } else {
        setReferralStatus('invalid');
        setReferredByUserId('');
      }
    } catch (err) {
      console.error('Error validating referral code:', err);
      setReferralStatus('invalid');
      setReferredByUserId('');
    }
  };

  // Step 4: Profile information and shipping address
  const validateAddress = () => {
    const errors = {};
    
    if (!shippingAddress.name.trim()) {
      errors.name = 'Name is required';
    }
    
    if (!shippingAddress.street1.trim()) {
      errors.street1 = 'Street address is required';
    }
    
    if (!shippingAddress.city.trim()) {
      errors.city = 'City is required';
    }
    
    if (!shippingAddress.state.trim()) {
      errors.state = 'State is required';
    }
    
    if (!shippingAddress.zip.trim()) {
      errors.zip = 'ZIP code is required';
    } else if (!/^\d{5}(-\d{4})?$/.test(shippingAddress.zip.trim())) {
      errors.zip = 'Invalid ZIP code format';
    }
    
    if (!shippingAddress.country.trim()) {
      errors.country = 'Country is required';
    }
    
    setAddressErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCompleteRegistration = async () => {
    if (!username.trim()) {
      setError('Please enter a username.');
      return;
    }

    // Validate city and state
    if (!shippingAddress.city.trim()) {
      setError('Please enter your city.');
      return;
    }
    if (!shippingAddress.state.trim()) {
      setError('Please enter your state.');
      return;
    }

  setCheckingVerification(true);
  setError('');

  try {
    const user = auth.currentUser;
    if (!user) {
        setError('No active user. Please log in again.');
      return;
    }

      // Validate username length
      if (username.length < 3 || username.length > 20) {
        setError('Username must be between 3 and 20 characters.');
        return;
      }

      if (isUsernameBlocklisted(username)) {
        setError('This username is not available.');
        return;
      }

      // Kick off the work that doesn't depend on anything else BEFORE awaiting
      // the username check. This step ran ~10 network round-trips strictly in
      // series and felt slow on a real device even with no photo picked
      // (Pixel 4a, Play build, 2026-08-21). Device fingerprinting and the
      // existing-user read have no relationship to username availability, so
      // they overlap with it now. Errors are captured, not thrown, so an
      // unawaited rejection can't become an unhandled promise while the
      // username check is still in flight; each is re-thrown at its await.
      const settle = (p) => p.then((value) => ({ value }), (error) => ({ error }));
      const fingerprintPromise = settle(
        import('../utils/deviceFingerprint').then(async ({ generateDeviceFingerprint, generateDeviceInfo }) => {
          const [fp, info] = await Promise.all([generateDeviceFingerprint(), generateDeviceInfo()]);
          return { deviceFingerprint: fp, deviceInfo: info };
        })
      );
      const existingUserPromise = settle(getDoc(doc(db, 'users', user.uid)));

      // Check username availability
      const usernameExists = await checkUsernameExists(username);
      if (usernameExists) {
        setError('This username is not available.');
      return;
    }

      // Upload photo if provided
      let photoURL = '';
      if (photo) {
        try {
          const originalSizeMB = photo.size / 1024 / 1024;
          console.log(`📸 Original photo size: ${originalSizeMB.toFixed(2)}MB`);

          // Defense-in-depth: same hard size cap that handlePhotoSelect
          // enforced at pick time. Catches any case where the file was set
          // in state via a path that bypassed the input handler.
          if (originalSizeMB > PROFILE_PHOTO_MAX_UPLOAD_SIZE_MB) {
            throw new Error(
              `Profile photo is too large (${originalSizeMB.toFixed(1)}MB). ` +
              `Maximum is ${PROFILE_PHOTO_MAX_UPLOAD_SIZE_MB}MB.`
            );
          }

          let photoToUpload = photo;

          // Always compress (even if file looks small) so HEIC files get
          // converted to JPEG. HEIC doesn't render in Chrome / Firefox /
          // Edge — only Safari — so leaving it un-converted produces broken
          // images for most viewers. Same rationale as AddPedalForm.jsx.
          try {
            const compressionOptions = {
              maxSizeMB: 0.5,
              maxWidthOrHeight: 800,
              useWebWorker: false,
              quality: 0.85,
              fileType: 'image/jpeg'
            };

            console.log(`🔄 Compressing profile photo...`);
            const compressionPromise = (async () => {
              try {
                return await imageCompression(photo, compressionOptions);
              } catch (err) {
                // browser-image-compression sometimes rejects with a raw
                // Event. Convert to a real Error so downstream catch logic
                // works.
                if (err instanceof Event) {
                  throw new Error(`Compression library error: ${err.type || 'unknown event'}`);
                }
                throw err;
              }
            })();
            const timeoutPromise = new Promise((_, reject) =>
              setTimeout(() => reject(new Error('Compression timeout after 30 seconds')), 30000)
            );
            photoToUpload = await Promise.race([compressionPromise, timeoutPromise]);

            const compressedSizeMB = photoToUpload.size / 1024 / 1024;
            const savings = ((1 - photoToUpload.size / photo.size) * 100).toFixed(1);
            console.log(`✅ Profile photo compressed: ${compressedSizeMB.toFixed(2)}MB (${savings}% smaller)`);
          } catch (compressionError) {
            console.error('❌ Profile photo compression failed:', compressionError);
            // Fallback: upload original ONLY if it's small enough. The
            // original bug was uploading multi-MB originals when
            // compression failed silently. Anything larger = hard error so
            // the user knows.
            if (originalSizeMB <= PROFILE_PHOTO_COMPRESSION_FALLBACK_MAX_MB) {
              console.warn(`⚠️ Compression failed — uploading original (${originalSizeMB.toFixed(2)}MB). Server-side autoCompressImages will retry.`);
              photoToUpload = photo;
            } else {
              throw new Error(
                `Could not compress your profile photo (${originalSizeMB.toFixed(1)}MB). ` +
                `Please choose a smaller photo or use a different image.`
              );
            }
          }

          const storageRef = ref(getStorage(), `profile_photos/${user.uid}`);
          await uploadBytes(storageRef, photoToUpload);
          photoURL = await getDownloadURL(storageRef);
        } catch (photoError) {
          console.error('❌ Error uploading profile photo:', photoError);
          // Previously: silently swallowed and signup continued without
          // photo. Now: surface the failure so the user can fix it. The
          // signup form shows `error` state below the submit button.
          setError(`Profile photo upload failed: ${photoError.message}`);
          return; // halt signup; user retries with a better photo
        }
      }

      // Device/IP information for ban prevention — started in parallel above.
      const fingerprintResult = await fingerprintPromise;
      if (fingerprintResult.error) throw fingerprintResult.error;
      const { deviceFingerprint, deviceInfo } = fingerprintResult.value;

      const displayUsername = username.trim();

      // Existing user data (preserves an already-verified phone number) —
      // also started in parallel above.
      const existingUserResult = await existingUserPromise;
      if (existingUserResult.error) throw existingUserResult.error;
      const existingUserDoc = existingUserResult.value;
      const existingUserData = existingUserDoc.exists() ? existingUserDoc.data() : {};
      
      // Generate unique referral code for this user: USERNAME-XXXX
      const generateReferralCode = (uname) => {
        const prefix = uname.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 10);
        const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // Avoid confusing chars (I,O,0,1)
        let suffix = '';
        for (let i = 0; i < 4; i++) {
          suffix += chars.charAt(Math.floor(Math.random() * chars.length));
        }
        return `${prefix}-${suffix}`;
      };

      const userReferralCode = generateReferralCode(displayUsername);

      // Update user document with all information
      // Only save city+state as partial shippingAddress at registration
      // Full shipping address (street, zip, etc.) is collected later on Profile page
      const updateData = {
        email: user.email,
        // username + usernameLowercase are set only via reserveUsername callable (server).
        photoURL,
        // Preserve existing phoneNumber if available, otherwise use confirmationResult or Firebase Auth
        phoneNumber: confirmationResult?.phoneNumber || existingUserData.phoneNumber || user.phoneNumber || null,
        shippingAddress: {
          city: capitalizeWords(shippingAddress.city.trim()),
          state: capitalizeWords(shippingAddress.state.trim())
        },
        createdAt: serverTimestamp(),
        isVerified: true,
        phoneVerified: true,
        emailVerified: true,
        profileComplete: true, // Mark profile as complete
        deviceFingerprint,
        deviceInfo,
        registrationIP: null,
        lastActiveIP: null,
        // accountStatus is set on initial doc creation (line 879) and is in the
        // firestore.rules deny-list — owner cannot self-write it on update.
        // Referral program fields
        referralCode: userReferralCode,
        // referralCredits is in the firestore.rules deny-list — owner cannot
        // self-write it. The default `|| 0` pattern at every read site makes
        // an absent field equivalent to 0, so we don't need to initialize here.
        referralCount: 0,
        referralCreditsClaimed: 0,
        // Early bird incentive fields
        earlyBirdEligible: true,
        earlyBirdCreditAwarded: false,
        earlyBirdModalShown: false,
        pendingReferralCode: deleteField(), // Clean up temp field from Step 1
        ...(referredByUserId ? { referredBy: referredByUserId, referralCodeUsed: referralCode.trim().toUpperCase() } : {})
      };
      
      console.log('🔍 Registration completion - updateData:', updateData);
      
      await setDoc(doc(db, 'users', user.uid), updateData, { merge: true });

      // Security Phase 1, Stage A safety net: re-assert phoneVerified
      // server-side (against Firebase Auth) in case the SMS-step call was
      // lost. Fire-and-forget — cannot block or fail registration.
      fireConfirmPhoneVerified('registrationComplete');

      const functions = getFunctions();
      const reserveUsernameFn = httpsCallable(functions, 'reserveUsername');
      await reserveUsernameFn({ username: username.trim() });

      // Create pending referral record if user entered a valid referral code
      if (referredByUserId) {
        try {
          await setDoc(doc(collection(db, 'referrals')), {
            referrerId: referredByUserId,
            referredUserId: user.uid,
            referralCode: referralCode.trim().toUpperCase(),
            status: 'pending',
            creditAwarded: false,
            createdAt: serverTimestamp()
          });
          console.log('✅ Referral record created for referrer:', referredByUserId);
        } catch (referralErr) {
          console.error('⚠️ Failed to create referral record:', referralErr);
          // Don't block registration for referral errors
        }
      }
      
      // Clean up referral code from localStorage now that registration is done
      localStorage.removeItem('pendingReferralCode');
      
      console.log('✅ Registration completion - Firestore write successful');
      
      // Verify what was actually written
      // The post-write read-back that used to sit here only existed to
      // console.log what had just been written — a full extra round-trip the
      // user waited through, on the slowest step of signup, for a log line
      // release builds don't even emit. setDoc having resolved is the
      // confirmation; UserContext's snapshot is the live source afterwards.

      setRegistrationComplete(true);
      // Navigate to onboarding step 5
      navigate('/onboarding-step-5');

  } catch (err) {
      console.error('Registration completion error:', err);
      const code = err?.code || '';
      const msg = err?.message || '';
      if (code === 'functions/failed-precondition' && msg.includes('USERNAME_NOT_AVAILABLE')) {
        setError('This username is not available.');
      } else if (code === 'functions/invalid-argument' && msg.includes('INVALID_USERNAME')) {
        setError('Username must be between 3 and 20 characters.');
      } else if (code === 'functions/unauthenticated') {
        setError('You must be signed in to complete registration.');
      } else {
        setError(`Failed to complete registration: ${msg || 'Unknown error'}`);
      }
  } finally {
    setCheckingVerification(false);
  }
};

  // Login flow - optimized for performance. Shared by the password form and
  // the native Face ID / Touch ID button.
  const loginWithCredentials = async (loginEmail, loginPassword, { fromBiometric = false } = {}) => {
  setError('');

  try {
      const userCredential = await signInWithEmailAndPassword(auth, loginEmail, loginPassword);
      const user = userCredential.user;

      // Check account status - keep this as it's critical for security
      const userDocRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userDocRef);
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        
        if (userData.accountStatus === 'banned') {
          await auth.signOut();
          setError('This account has been banned or disabled. Please contact us at contact@pedalxchange.com');
          return;
        }
        // Auto-suspended pending admin review (Phase D-lite). Generic
        // wording on purpose: do not name the matched signal, otherwise
        // bypasses become easy to iterate.
        if (userData.accountStatus === 'suspended_pending_review') {
          await auth.signOut();
          setError('Your account has been suspended pending review. If you believe this is a mistake, please contact contact@pedalxchange.com.');
          return;
        }
        // ('suspended' status check removed 2026-04-28 — see sign-up flow above.)
        
        // Check if user has completed all verification steps before allowing login
        const isEmailVerified = user.emailVerified;
        const isPhoneVerified = userData.phoneVerified;
        const isFullyVerified = userData.isVerified;
        const hasCompleteProfile = userData.profileComplete === true || (userData.username && userData.shippingAddress?.street1);
        
        // If verification is incomplete, redirect to continue signup flow
        if (!isEmailVerified || !isPhoneVerified || !isFullyVerified || !hasCompleteProfile) {
          // KEEP USER SIGNED IN so they can complete verification steps
          // DO NOT sign out - phone auth requires an authenticated user
          
          // Set state to continue from where they left off
          setIsSignUp(true);
          
          if (!isEmailVerified) {
            setCurrentStep(2);
            setEmailSent(true);
            setError('Your account verification is incomplete. Please check your email and click the verification link to continue.');
          } else if (!isPhoneVerified) {
            setEmailVerified(true);
            setCurrentStep(3);
            setError('Your account verification is incomplete. Please complete phone verification.');
          } else if (!hasCompleteProfile || !userData.username) {
            setEmailVerified(true);
            setPhoneVerified(true);
            setCurrentStep(4);
            setError('Your account verification is incomplete. Please complete your profile.');
          }
          
          return; // Don't throw error, just redirect to continue signup
        }
      }

      // Capture IP in background - don't wait for it
      const functions = getFunctions();
      const captureIP = httpsCallable(functions, 'captureUserIP');
      captureIP({}).catch(ipError => {
        console.warn('⚠️ Failed to capture login IP (non-critical):', ipError);
      });

      // Remember the account for next time so the email field comes prefilled.
      rememberLastLoginEmail(loginEmail);

      // Prompt browser to save credentials
      try {
        if (window.PasswordCredential) {
          const cred = new window.PasswordCredential({ id: loginEmail, password: loginPassword });
          await navigator.credentials.store(cred);
        }
      } catch (credErr) {
        // Non-critical — don't block login
      }

      // Native app: remember this login for Face ID / Touch ID, and offer
      // (once per email) to save it to iCloud Keychain so it autofills in
      // Safari too. Both are fire-and-forget and never block navigation.
      if (Capacitor.isNativePlatform()) {
        saveBiometricLogin(loginEmail, loginPassword);
        maybePromptSavePasswordToKeychain(loginEmail, loginPassword);
      }

      // Check if there's a return URL from before login
      const returnUrl = sessionStorage.getItem('returnAfterLogin');
      if (returnUrl) {
        console.log('🔄 Redirecting back to:', returnUrl);
        sessionStorage.removeItem('returnAfterLogin');
        navigate(returnUrl);
      } else {
        // Navigate to dashboard by default
        navigate('/dashboard');
      }
  } catch (err) {
      console.error('Login error:', err.message, '| Code:', err.code);

    // Saved Face ID credentials no longer match (password was changed):
    // clear them and ask for a manual login, which re-saves fresh ones.
    if (fromBiometric && ['auth/invalid-credential', 'auth/wrong-password', 'auth/user-not-found'].includes(err.code)) {
      clearBiometricLogin();
      setBioSavedLogin(false);
      setError('Your saved login is out of date. Please log in with your password to update it.');
      return;
    }

    // Check for specific Firebase error codes and provide user-friendly messages
    if (err.code === 'auth/user-not-found') {
      setError('No account found with this email. Please sign up to create a new account.');
    } else if (err.code === 'auth/invalid-credential') {
      setError('Invalid email or password. If you don\'t have an account yet, please sign up below. Otherwise, use "Forgot Password" to reset it.');
    } else if (err.code === 'auth/wrong-password') {
      setError('Incorrect password. Please try again or use "Forgot Password" below to reset it.');
    } else if (err.code === 'auth/invalid-email') {
      setError('Please enter a valid email address.');
    } else if (err.code === 'auth/too-many-requests') {
      setError('Too many failed login attempts. Please wait 15 minutes and try again, or use "Forgot Password" to reset your password.');
    } else if (err.code === 'auth/network-request-failed') {
      setError('Network connection issue. Please check your internet connection and try again.');
    } else if (err.code === 'auth/user-disabled') {
      setError('This account has been disabled. Please contact support for assistance.');
    } else if (err.message?.includes('blocked')) {
      setError('Account access blocked. Please contact support. Error code: ' + (err.code || 'BLOCKED'));
    } else {
      setError('Login failed: ' + (err.message || 'Unknown error') + '. Please contact support if this continues. Error code: ' + (err.code || 'UNKNOWN'));
    }
  }
};

  const handleLogin = async (e) => {
    e.preventDefault();
    if (previewMode) return;
    await loginWithCredentials(email, password);
  };

  const handleBiometricLogin = async () => {
    if (previewMode || biometricLoggingIn) return;
    setError('');
    setBiometricLoggingIn(true);
    try {
      const saved = await getBiometricLogin();
      if (saved) {
        await loginWithCredentials(saved.email, saved.password, { fromBiometric: true });
      } else {
        // Either the user cancelled the scan, or the Keychain entry is gone
        // (e.g. cleared by a reinstall). Re-check so a dead button disappears.
        setBioSavedLogin(await hasSavedBiometricLogin());
      }
    } catch (err) {
      setError('Face ID login failed. Please log in with your password.');
    } finally {
      setBiometricLoggingIn(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (previewMode || resetSending) return;
    setError('');
    
    if (!email) {
      setError('Please enter your email address');
      return;
    }

    setResetSending(true);

    try {
      const response = await fetch('https://us-central1-pedalxchange.cloudfunctions.net/securePasswordReset', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: email.toLowerCase() })
      });

      const result = await response.json();

      if (result.success) {
        setResetEmailSent(true);
        setError(''); // Clear any previous errors
      } else {
        // Handle specific error cases
        if (response.status === 429) {
          // Rate limiting
          const retryAfter = result.retryAfter || 900; // Default to 15 minutes
          const minutes = Math.ceil(retryAfter / 60);
          setError(`Too many password reset attempts. Please try again in ${minutes} minutes.`);
        } else {
          // Other errors (user not found, banned, etc.)
          setError(result.error || 'Failed to send reset email. Please try again.');
        }
      }
    } catch (err) {
      console.error('Password reset error:', err.message, '| Code:', err.code);
      
      // Provide user-friendly error messages
      if (err.code === 'auth/user-not-found') {
        setError('No account found with this email. Please check your email or sign up to create a new account.');
      } else if (err.code === 'auth/invalid-email') {
        setError('Invalid email address. Please check your email and try again.');
      } else if (err.code === 'auth/too-many-requests') {
        setError('Too many password reset attempts. Please wait a few minutes and try again.');
      } else if (err.code === 'auth/network-request-failed') {
        setError('Network connection issue. Please check your internet and try again.');
      } else {
        setError('Failed to send reset email: ' + (err.message || 'Unknown error') + '. Please contact support if this continues.');
      }
    } finally {
      setResetSending(false);
    }
  };

  console.log('🔍 CURRENT RENDER STATE:', {
    isSignUp,
    currentStep,
    emailVerified,
    phoneVerified
  });

  return (
  <>
    <style>{`
      @media (max-width: 1024px) {
        body {
          padding-bottom: 0 !important;
        }
      }
    `}</style>
    <div>
    {previewMode && (
      <div style={{ 
        position: 'fixed', 
        top: '10px', 
        right: '10px', 
        backgroundColor: '#f0f0f0', 
        padding: '10px', 
        borderRadius: '8px', 
        border: '2px solid #333',
        zIndex: 9999 
      }}>
        <div style={{ marginBottom: '10px', fontWeight: 'bold' }}>Preview Mode</div>
        <div style={{ display: 'flex', gap: '5px', flexDirection: 'column' }}>
          <button onClick={() => { setIsSignUp(false); setCurrentStep(1); }} style={{ padding: '5px 10px', borderRadius: '4px', border: '1px solid #333', cursor: 'pointer' }}>Login</button>
          <button onClick={() => { setIsSignUp(true); setCurrentStep(1); }} style={{ padding: '5px 10px', borderRadius: '4px', border: '1px solid #333', cursor: 'pointer' }}>Step 1</button>
          <button onClick={() => { setIsSignUp(true); setCurrentStep(2); setEmailVerified(false); }} style={{ padding: '5px 10px', borderRadius: '4px', border: '1px solid #333', cursor: 'pointer' }}>Step 2</button>
          <button onClick={() => { setIsSignUp(true); setCurrentStep(3); setEmailVerified(true); }} style={{ padding: '5px 10px', borderRadius: '4px', border: '1px solid #333', cursor: 'pointer' }}>Step 3</button>
          <button onClick={() => { setIsSignUp(true); setCurrentStep(4); setEmailVerified(true); setPhoneVerified(true); }} style={{ padding: '5px 10px', borderRadius: '4px', border: '1px solid #333', cursor: 'pointer' }}>Step 4</button>
        </div>
      </div>
    )}
    <h2>{isSignUp ? 'Sign Up' : 'Sign In'}</h2>

      {!isSignUp ? (
        // Login form
        <div>
          {!showForgotPassword ? (
            // Regular login form
            <div style={{ maxWidth: '400px', margin: '0 auto', padding: '0 1rem' }}>
              <form onSubmit={handleLogin} autoComplete="on">
                <input
                  type="email"
                  name="email"
                  autoComplete="username"
                  placeholder="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="border border-gray-300 focus:border-blue-500 focus:outline-none"
                  style={{ borderRadius: '25px', fontSize: '16px', padding: '11px 16px', width: '100%', marginBottom: '12px', boxSizing: 'border-box' }}
                />
                <input
                  type="password"
                  name="password"
                  autoComplete="current-password"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="border border-gray-300 focus:border-blue-500 focus:outline-none"
                  style={{ borderRadius: '25px', fontSize: '16px', padding: '11px 16px', width: '100%', marginBottom: '12px', boxSizing: 'border-box' }}
                />
                <button 
                  type="submit"
                  className="hover:opacity-80 transition-opacity"
                  style={{
                    backgroundColor: '#C9A227',
                    color: 'white',
                    border: 'none',
                    borderRadius: '9999px',
                    padding: '11px 32px',
                    fontFamily: 'Nunito, sans-serif',
                    fontWeight: 'bold',
                    fontSize: '16px',
                    cursor: 'pointer',
                    display: 'block',
                    margin: isNarrowScreen ? '18px auto 0' : '0 auto'
                  }}
                >
                  Log In
                </button>
              </form>
              {bioLabel && bioSavedLogin && (
                <button
                  type="button"
                  onClick={handleBiometricLogin}
                  disabled={biometricLoggingIn}
                  className="hover:opacity-80 transition-opacity"
                  style={{
                    backgroundColor: 'white',
                    color: '#C9A227',
                    border: '2px solid #C9A227',
                    borderRadius: '9999px',
                    padding: '9px 32px',
                    fontFamily: 'Nunito, sans-serif',
                    fontWeight: 'bold',
                    fontSize: '16px',
                    cursor: biometricLoggingIn ? 'wait' : 'pointer',
                    opacity: biometricLoggingIn ? 0.7 : 1,
                    display: 'block',
                    margin: '12px auto 0'
                  }}
                >
                  {biometricLoggingIn ? 'Checking…' : `Log in with ${bioLabel}`}
                </button>
              )}
              {bioLabel && bioSavedLogin && bioSavedEmail && (
                <p style={{
                  fontSize: '13px',
                  color: '#6b7280',
                  textAlign: 'center',
                  marginTop: '6px'
                }}>
                  Signs in as {maskEmail(bioSavedEmail)}
                </p>
              )}
              <div style={{ textAlign: 'center', marginTop: '2rem' }}>
                <button 
                  type="button" 
                  onClick={() => setShowForgotPassword(true)}
                  style={{ 
                    background: 'none', 
                    border: 'none', 
                    color: '#5A9ECB', 
                    cursor: 'pointer', 
                    fontFamily: 'inherit',
                    fontSize: 'inherit'
                  }}
                >
                  Forgot Password?
                </button>
              </div>
            </div>
          ) : (
            // Forgot password form
            <div style={{ maxWidth: '400px', margin: '0 auto', padding: '0 1rem' }}>
              <h3>Reset Password</h3>
              {!resetEmailSent ? (
                <div>
                  <p>Enter your email address and we'll send you a link to reset your password.</p>
                  <form onSubmit={handleForgotPassword}>
                    <input
                      type="email"
                      placeholder="Email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="border border-gray-300 focus:border-blue-500 focus:outline-none"
                      style={{ borderRadius: '25px', fontSize: '16px', padding: '11px 16px', width: '100%', marginBottom: '12px', boxSizing: 'border-box' }}
                    />
                    <button 
                      type="submit"
                      disabled={resetSending}
                      className="hover:opacity-80 transition-opacity"
                      style={{
                        backgroundColor: resetSending ? '#a0a0a0' : '#C9A227',
                        color: 'white',
                        border: 'none',
                        borderRadius: '9999px',
                        padding: '8px 24px',
                        fontFamily: 'Nunito, sans-serif',
                        fontWeight: 'bold',
                        fontSize: '15px',
                        cursor: resetSending ? 'not-allowed' : 'pointer',
                        display: 'block',
                        margin: '0 auto',
                        opacity: resetSending ? 0.7 : 1
                      }}
                    >
                      {resetSending ? 'Sending...' : 'Send Reset Email'}
                    </button>
                  </form>
                  <div style={{ textAlign: 'center', marginTop: '1rem' }}>
                    <button 
                      type="button" 
                      onClick={() => {
                        setShowForgotPassword(false);
                        setResetEmailSent(false);
                        setError('');
                      }}
                      style={{ 
                        background: 'none', 
                        border: 'none', 
                        color: '#5A9ECB', 
                        cursor: 'pointer', 
                        fontFamily: 'inherit',
                        fontSize: 'inherit'
                      }}
                    >
                      Back to Login
                    </button>
                  </div>
                </div>
              ) : (
                <div>
                  <p style={{ color: 'green' }}>
                    ✅ Reset email sent! Check your inbox and follow the instructions to reset your password.
                  </p>
                  <p>Didn't receive the email? Check your spam folder or try again.</p>
                  <div style={{ textAlign: 'center', marginTop: '1rem' }}>
                    <button 
                      type="button" 
                      onClick={() => {
                        setShowForgotPassword(false);
                        setResetEmailSent(false);
                        setError('');
                      }}
                      style={{ 
                        background: 'none', 
                        border: 'none', 
                        color: '#5A9ECB', 
                        cursor: 'pointer', 
                        fontFamily: 'inherit',
                        fontSize: 'inherit'
                      }}
                    >
                      Back to Login
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      ) : (
        // Signup flow
        <div>
          {/* Step 1: Email and Password */}
          {currentStep === 1 && (
            <div style={{ maxWidth: '400px', margin: '0 auto', padding: '0 1rem' }}>
              <h3>Step 1: Create Account</h3>
              <p>Enter your email and password to get started.</p>
              <p style={{ fontSize: '14px', color: '#666', fontStyle: 'italic', marginTop: '8px', marginBottom: '12px' }}>
                🇺🇸 Currently available to US-based users only
              </p>
              <form onSubmit={handleEmailPasswordSubmit} autoComplete="on">
                <input
                  type="email"
                  name="email"
                  autoComplete="email"
                  placeholder="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={isCreatingAccount}
                  className="border border-gray-300 focus:border-blue-500 focus:outline-none"
                  style={{ borderRadius: '25px', fontSize: '16px', padding: '11px 16px', width: '100%', marginBottom: '12px', boxSizing: 'border-box' }}
                />
                <input
                  type="password"
                  name="password"
                  autoComplete="new-password"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={isCreatingAccount}
                  className="border border-gray-300 focus:border-blue-500 focus:outline-none"
                  style={{ borderRadius: '25px', fontSize: '16px', padding: '11px 16px', width: '100%', marginBottom: '12px', boxSizing: 'border-box' }}
                />
                <button 
                  type="submit"
                  disabled={isCreatingAccount}
                  className="hover:opacity-80 transition-opacity"
                  style={{
                    backgroundColor: '#C9A227',
                    color: 'white',
                    border: 'none',
                    borderRadius: '9999px',
                    padding: '11px 32px',
                    fontFamily: 'Nunito, sans-serif',
                    fontWeight: 'bold',
                    fontSize: '16px',
                    cursor: isCreatingAccount ? 'not-allowed' : 'pointer',
                    opacity: isCreatingAccount ? 0.7 : 1,
                    display: 'block',
                    margin: '0 auto'
                  }}
                >
                  {isCreatingAccount ? 'Creating Account & Sending Email...' : 'Create Account'}
                </button>
              </form>
            </div>
          )}

          {/* Step 2: Email Verification */}
          {currentStep === 2 && (
            <div style={{ marginTop: '2rem', padding: '1rem', maxWidth: '600px', margin: '2rem auto' }}>
              <h3>Step 2: Verify Your Email</h3>
              <p>We've sent a verification email to <strong>{email}</strong>.</p>
              <p>Please check your inbox and spam folder, then click the verification link in the email.</p>

              {checkingVerification && (
                <div style={{ marginTop: '1rem', padding: '0.75rem 1rem', backgroundColor: '#d4edda', color: '#155724', borderRadius: '12px', maxWidth: '500px', margin: '1rem auto', textAlign: 'center', fontWeight: 600 }}>
                  Confirming your email verification…
                </div>
              )}

              {!emailVerified && (
                <div style={{ marginTop: '1rem', padding: '1rem', backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)', maxWidth: '500px', margin: '1rem auto', textAlign: 'center' }}>
                  <p><strong>Next Steps:</strong></p>
                  <ol style={{ textAlign: 'left', paddingLeft: '1.5rem', marginLeft: 0, display: 'inline-block' }}>
                    <li>Check your email inbox (and spam folder)</li>
                    <li>Click the verification link in the email</li>
                    <li>You'll be automatically redirected back here</li>
                    <li>Continue with phone verification</li>
                  </ol>
                  
                  <div style={{ marginTop: '1rem', padding: '1rem', backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)' }}>
                    <p><strong>Didn't receive the email?</strong></p>
                    <style>{`
                      .pxc-verify-btn {
                        padding: 0.5rem 1rem;
                        font-size: 1rem;
                        line-height: 1.2;
                      }
                      @media (max-width: 768px) {
                        .pxc-verify-btn {
                          padding: 0.85rem 1.75rem;
                          font-size: 1.0625rem;
                        }
                      }
                    `}</style>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', alignItems: 'center' }}>
                      <button
                        type="button" 
                        onClick={async () => {
                          try {
                            const user = auth.currentUser;
                            if (!user || !user.email) {
                              setError('No user session found. Please refresh the page and try again.');
                              return;
                            }

                            console.log('📧 Resending verification email to:', user.email);
                            
                            // Reload user to refresh token if expired
                            try {
                              await user.reload();
                              console.log('✅ User session refreshed');
                            } catch (reloadError) {
                              console.warn('⚠️ Could not refresh user session:', reloadError);
                              // Continue anyway
                            }
                            
                            // Use the SAME custom email as initial signup for consistency
                            try {
                              const functions = getFunctions();
                              const sendCustomEmailVerification = httpsCallable(functions, 'sendCustomEmailVerification');
                              const continueUrl = getEmailContinueUrl();
                              
                              await sendCustomEmailVerification({ email: user.email, continueUrl });
                              console.log('✅ Custom verification email resent successfully');
                              alert('Verification email resent! Please check your inbox and spam folder.');
                            } catch (customError) {
                              console.warn('⚠️ Custom email failed, falling back to Firebase default:', customError);
                              
                              // Fallback to Firebase's default verification email
                              const actionCodeSettings = {
                                url: `${getEmailLinkOrigin()}/signup?emailVerified=true`,
                                handleCodeInApp: false  // Let Firebase handle it, then redirect
                              };
                              
                              await sendEmailVerification(user, actionCodeSettings);
                              console.log('✅ Fallback verification email resent successfully');
                              alert('Verification email resent! Please check your inbox and spam folder.');
                            }
                          } catch (error) {
                            console.error('❌ Resend email error:', error);
                            console.error('❌ Error code:', error.code);
                            console.error('❌ Error message:', error.message);
                            
                            // User-friendly error messages
                            if (error.code === 'auth/user-token-expired' || error.code === 'auth/user-not-found') {
                              setError('Your session has expired. Please refresh the page and log in again.');
                            } else if (error.code === 'auth/too-many-requests') {
                              setError('Too many requests. Please wait a few minutes before trying again.');
                            } else {
                              setError('Failed to resend verification email. Please try again or contact support at contact@pedalxchange.com');
                            }
                          }
                        }}
                        className="hover:opacity-80 transition-opacity pxc-verify-btn"
                        style={{ 
                          backgroundColor: '#C9A227', 
                          color: 'white', 
                          border: 'none', 
                          borderRadius: '9999px',
                          cursor: 'pointer',
                          fontWeight: 'bold',
                          fontFamily: 'Nunito, sans-serif'
                        }}
                      >
                        Resend Verification Email
                      </button>
                      
                      <button 
                        type="button" 
                        onClick={async () => {
                          try {
                            const user = auth.currentUser;
                            if (user) {
                              await user.reload();
                              console.log('🔍 Manual check - user.emailVerified:', user.emailVerified);
                              
                              if (user.emailVerified) {
                                // Update Firestore to match
                                const userDocRef = doc(db, 'users', user.uid);
                                await setDoc(userDocRef, {
                                  emailVerified: true,
                                  emailVerifiedAt: serverTimestamp()
                                }, { merge: true });
                                console.log('✅ Firestore updated with emailVerified: true');
                                
                                setEmailVerified(true);
                                setCurrentStep(3);
                                alert('Email verified successfully! Please continue with phone verification.');
                              } else {
                                setError('Email not verified yet. Please check your inbox and click the verification link.');
                              }
                            }
                          } catch (error) {
                            console.error('Error checking verification:', error);
                            setError('Failed to check email verification status. Please try again.');
                          }
                        }}
                        className="pxc-verify-btn"
                        style={{ 
                          backgroundColor: '#E5E7EB', 
                          color: '#374151', 
                          border: 'none', 
                          borderRadius: '9999px',
                          cursor: 'pointer',
                          fontWeight: 'normal'
                        }}
                      >
                        Check Email Verification
                      </button>
                    </div>
                  </div>
                </div>
              )}
              
              {emailVerified && (
                <div style={{ marginTop: '1rem', padding: '1rem', backgroundColor: '#d4edda', borderRadius: '5px', color: '#155724' }}>
                  <p><strong>✓ Email Verified Successfully!</strong></p>
                  <p>You can now continue with phone verification.</p>
                </div>
              )}
            </div>
          )}

          {/* Step 3: Phone Verification */}
          {currentStep === 3 && (
      <div style={{ marginTop: '2rem', padding: '1rem', border: '1px solid #ccc', borderRadius: '5px', maxWidth: '500px', margin: '2rem auto' }}>
              <h3>Step 3: Phone Verification</h3>
              <p>🇺🇸 <strong>US Residents Only:</strong> PedalxChange is currently available to US residents only. Please enter a valid US phone number to continue.</p>
              <p style={{ fontSize: '15px', color: '#C9A227', marginTop: '10px' }}>
                Your information is never shared or sold. Phone verification helps us maintain a trusted, high-quality community and protects every member — new and existing — from fraudulent accounts.
              </p>
        
        <div style={{ marginBottom: '1rem', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <input
            type="tel"
            placeholder="Phone number (e.g., +1234567890)"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            required
            className="border border-gray-300 focus:border-blue-500 focus:outline-none"
            style={{ borderRadius: '25px', fontSize: '16px', padding: '11px 16px', width: '100%', maxWidth: '400px', marginBottom: '12px', boxSizing: 'border-box' }}
          />
          <button 
            type="button" 
            onClick={handleSendCode}
            disabled={!phone || sendingCode}
            className="hover:opacity-80 transition-opacity"
            style={{
              backgroundColor: !phone || sendingCode ? '#9CA3AF' : '#C9A227',
              color: 'white',
              border: 'none',
              borderRadius: '9999px',
              padding: '11px 32px',
              fontFamily: 'Nunito, sans-serif',
              fontWeight: 'bold',
              fontSize: '16px',
              cursor: !phone || sendingCode ? 'not-allowed' : 'pointer',
              display: 'block',
              margin: '0 auto',
              opacity: sendingCode ? 0.7 : 1
            }}
          >
            {sendingCode ? 'Sending…' : 'Send SMS Code'}
          </button>
        </div>

        {confirmationResult && (
          <div style={{ marginBottom: '1rem', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <input
              type="text"
              placeholder="Enter 6-digit SMS code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              maxLength={6}
              required
              className="border border-gray-300 focus:border-blue-500 focus:outline-none"
              style={{ borderRadius: '25px', fontSize: '16px', padding: '11px 16px', width: '100%', maxWidth: '280px', marginBottom: '12px', boxSizing: 'border-box' }}
            />
            <button 
              type="button" 
              onClick={handleVerifyCode}
              disabled={!code || code.length !== 6}
              className="hover:opacity-80 transition-opacity"
              style={{
                backgroundColor: (!code || code.length !== 6) ? '#9CA3AF' : '#C9A227',
                color: 'white',
                border: 'none',
                borderRadius: '9999px',
                padding: '11px 32px',
                fontFamily: 'Nunito, sans-serif',
                fontWeight: 'bold',
                fontSize: '16px',
                cursor: (!code || code.length !== 6) ? 'not-allowed' : 'pointer',
                display: 'block',
                margin: '0 auto'
              }}
            >
              Verify Code
            </button>
          </div>
        )}

        {phoneVerified && (
          <div style={{ marginBottom: '1rem' }}>
            <p style={{ color: '#10b981', fontWeight: 600 }}>✓ Phone verified!</p>
            <button 
              type="button" 
              onClick={() => {
                console.log('🚀 CONTINUE BUTTON CLICKED - ADVANCING TO STEP 4');
                setCurrentStep(4);
              }}
              style={{ 
                backgroundColor: '#C9A227', 
                color: 'white', 
                padding: '0.75rem 1.5rem', 
                border: 'none', 
                borderRadius: '9999px', 
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: 'bold',
                fontFamily: 'Nunito, sans-serif',
                marginTop: '0.5rem'
              }}
            >
              Continue to Complete Profile
            </button>
          </div>
        )}





        <div id="recaptcha-container"></div>
        
        {/* Delete Incomplete Account Button */}
        <div style={{ marginTop: '2rem', paddingTop: '1.5rem', borderTop: '1px solid #E5E7EB', textAlign: 'center' }}>
          <p style={{ fontSize: '14px', color: '#6B7280', marginBottom: '0.75rem' }}>
            Unable to complete verification?
          </p>
          <button 
            type="button" 
            disabled={isDeletingAccount}
            onClick={async () => {
              if (window.confirm('Are you sure you want to delete your incomplete account? This action cannot be undone.')) {
                setIsDeletingAccount(true);
                try {
                  const user = auth.currentUser;
                  if (!user) {
                    alert('No active user session found.');
                    setIsDeletingAccount(false);
                    return;
                  }
                  
                  console.log('🗑️ Deleting incomplete account:', user.uid);
                  
                  // Call deleteUserAccount cloud function to properly clean up
                  const functions = getFunctions();
                  const deleteAccount = httpsCallable(functions, 'deleteUserAccount');
                  await deleteAccount({ deletionReason: 'User requested deletion of incomplete account' });
                  
                  console.log('✅ Account deleted successfully');
                  setIsDeletingAccount(false);
                  alert('Your account has been deleted. You can sign up again in the future when you have a US phone number.');
                  
                  // Sign out and redirect to home
                  await auth.signOut();
                  navigate('/');
                } catch (error) {
                  console.error('❌ Error deleting account:', error);
                  setIsDeletingAccount(false);
                  alert('Failed to delete account. Please contact support at contact@pedalxchange.com');
                }
              }
            }}
            className="hover:opacity-70 transition-opacity"
            style={{
              backgroundColor: '#9CA3AF',
              color: 'white',
              border: 'none',
              borderRadius: '9999px',
              padding: '10px 28px',
              fontFamily: 'Nunito, sans-serif',
              fontWeight: 'bold',
              fontSize: '15px',
              cursor: isDeletingAccount ? 'not-allowed' : 'pointer',
              opacity: isDeletingAccount ? 0.7 : 1
            }}
          >
            {isDeletingAccount ? 'Deleting Account...' : 'Delete Incomplete Account'}
          </button>
        </div>
      </div>
    )}

          {/* Step 4: Profile Information */}
          {currentStep === 4 && (
      <div style={{ marginTop: '2rem', padding: '1.5rem', border: '1px solid #ccc', borderRadius: '8px', maxWidth: '600px', margin: '2rem auto' }}>
              <h3 style={{ textAlign: 'center', marginBottom: '0.5rem' }}>Step 4: Complete Your Profile</h3>
              <p style={{ textAlign: 'center', marginBottom: '1.5rem' }}>Please provide your profile information.</p>
              
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem' }}>Username:</label>
                <input
                  type="text"
                  placeholder="Username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  style={{ width: '100%', padding: '0.5rem', borderRadius: '25px', border: '1px solid #ccc', boxSizing: 'border-box', fontSize: '16px' }}
                  maxLength={20}
                  required
                />
                {username.length >= 3 && usernameAvailability === 'checking' && (
                  <span style={{ color: '#6b7280', fontSize: '0.875rem', display: 'block', marginTop: '0.25rem' }}>
                    Checking availability...
                  </span>
                )}
                {username.length >= 3 && usernameAvailability === 'available' && (
                  <span style={{ color: '#10b981', fontSize: '0.875rem', display: 'block', marginTop: '0.25rem' }}>
                    ✓ Username is available
                  </span>
                )}
                {username.length >= 3 && usernameAvailability === 'taken' && (
                  <span style={{ color: '#ef4444', fontSize: '0.875rem', display: 'block', marginTop: '0.25rem' }}>
                    ✗ This username is not available
                  </span>
                )}
                {username.length > 0 && username.length < 3 && (
                  <span style={{ color: '#6b7280', fontSize: '0.875rem', display: 'block', marginTop: '0.25rem' }}>
                    Username must be at least 3 characters
                  </span>
                )}
              </div>
        
        <div style={{ marginBottom: '1.5rem', textAlign: 'center' }}>
          <label style={{ display: 'block', marginBottom: '1rem', textAlign: 'center', fontSize: '1rem', fontWeight: '500' }}>Profile Photo (optional):</label>
          <input
            id="profile-photo-upload"
            type="file"
            accept="image/*"
            onChange={handlePhotoSelect}
            style={{ display: 'none' }}
          />
          <button
            type="button"
            onClick={() => document.getElementById('profile-photo-upload').click()}
            style={{
              backgroundColor: '#C9A227',
              color: 'white',
              padding: '14px 32px',
              border: 'none',
              borderRadius: '25px',
              fontSize: '1.125rem',
              fontWeight: 'bold',
              cursor: 'pointer',
              fontFamily: 'Nunito, sans-serif',
              transition: 'opacity 0.2s'
            }}
            onMouseEnter={(e) => e.target.style.opacity = '0.9'}
            onMouseLeave={(e) => e.target.style.opacity = '1'}
          >
            Choose File
          </button>
          {photo ? (
            <div style={{ marginTop: '0.75rem', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' }}>
              {photoPreview && (
                <img
                  src={photoPreview}
                  alt="Selected profile preview"
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  style={{ width: '72px', height: '72px', borderRadius: '50%', objectFit: 'cover', border: '2px solid #C9A227' }}
                />
              )}
              <p style={{ margin: 0, color: '#10b981', fontSize: '0.9375rem', fontFamily: 'Nunito, sans-serif' }}>
                ✓ {photo.name}
              </p>
            </div>
          ) : (
            <p style={{ marginTop: '0.75rem', color: '#6b7280', fontSize: '0.9375rem', fontFamily: 'Nunito, sans-serif' }}>
              No file selected
            </p>
          )}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem' }}>City:</label>
            <input
              type="text"
              placeholder="City"
              value={shippingAddress.city}
              onChange={e => setShippingAddress({ ...shippingAddress, city: capitalizeWords(e.target.value) })}
              style={{ width: '100%', padding: '0.5rem', borderRadius: '25px', border: '1px solid #ccc', boxSizing: 'border-box', fontSize: '16px' }}
              required
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem' }}>State:</label>
            <input
              type="text"
              placeholder="State"
              value={shippingAddress.state}
              onChange={e => setShippingAddress({ ...shippingAddress, state: capitalizeWords(e.target.value) })}
              style={{ width: '100%', padding: '0.5rem', borderRadius: '25px', border: '1px solid #ccc', boxSizing: 'border-box', fontSize: '16px' }}
              required
            />
          </div>
        </div>

        {/* Referral Code (Optional) */}
        <div style={{ marginBottom: '1.5rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', color: '#6B7280', fontSize: '0.9rem' }}>
            Referral Code (optional):
          </label>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <input
              type="text"
              placeholder="Enter referral code"
              value={referralCode}
              onChange={e => {
                setReferralCode(e.target.value.toUpperCase());
                setReferralStatus('');
                setReferredByUserId('');
              }}
              style={{
                flex: 1,
                minWidth: 0,
                padding: '11px 16px',
                borderRadius: '25px',
                border: `1px solid ${referralStatus === 'valid' ? '#22c55e' : referralStatus === 'invalid' ? '#ef4444' : '#ccc'}`,
                boxSizing: 'border-box',
                fontSize: '16px'
              }}
            />
            <button
              type="button"
              onClick={handleValidateReferralCode}
              disabled={!referralCode.trim() || referralStatus === 'checking'}
              style={{
                backgroundColor: '#C9A227',
                color: 'white',
                border: 'none',
                borderRadius: '9999px',
                // Sized to the standard button scale; flexShrink 0 + the
                // flex row (input is flex:1 with minWidth 0) means the input
                // narrows instead of the button ever wrapping beneath it.
                padding: '11px 24px',
                fontFamily: 'Nunito, sans-serif',
                fontWeight: 'bold',
                fontSize: '15px',
                flexShrink: 0,
                cursor: !referralCode.trim() || referralStatus === 'checking' ? 'not-allowed' : 'pointer',
                opacity: !referralCode.trim() || referralStatus === 'checking' ? 0.5 : 1,
                whiteSpace: 'nowrap'
              }}
            >
              {referralStatus === 'checking' ? '...' : 'Apply'}
            </button>
          </div>
          {referralStatus === 'valid' && (
            <p style={{ color: '#22c55e', fontSize: '0.8rem', marginTop: '0.25rem', fontWeight: '600' }}>
              Valid referral code applied
            </p>
          )}
          {referralStatus === 'invalid' && (
            <p style={{ color: '#ef4444', fontSize: '0.8rem', marginTop: '0.25rem', fontWeight: '600' }}>
              Invalid referral code
            </p>
          )}
        </div>

        <div style={{ textAlign: 'center' }}>
          <button 
            type="button" 
            onClick={handleCompleteRegistration}
            disabled={checkingVerification}
            style={{ 
              backgroundColor: '#C9A227', 
              color: 'white', 
              padding: '0.75rem 2rem',
              border: 'none',
              borderRadius: '9999px',
              fontSize: '1rem',
              fontWeight: 'bold',
              fontFamily: 'Nunito, sans-serif',
              cursor: checkingVerification ? 'not-allowed' : 'pointer',
              opacity: checkingVerification ? 0.7 : 1
            }}
          >
            {checkingVerification ? 'Saving...' : 'Next'}
          </button>
        </div>
            </div>
          )}
      </div>
    )}

    {/* Toggle link */}
    <p
      onClick={() => {
        // Heading into sign-up: drop the prefilled email (but keep anything
        // the user actually typed).
        if (!isSignUp && email && email === prefilledEmailRef.current) {
          setEmail('');
          prefilledEmailRef.current = '';
        }
        setIsSignUp(!isSignUp);
        setError('');
          setCurrentStep(1);
          setEmailSent(false);
          setEmailVerified(false);
          setPhoneVerified(false);
          setRegistrationComplete(false);
      }}
      style={{ cursor: 'pointer', color: '#5A9ECB', marginTop: '1rem' }}
    >
      {isSignUp ? 'Already have an account? Sign in' : 'Need an account? Sign up'}
    </p>

    {/* Show any error messages */}
    {error && (
      <p style={{
        color: 'red',
        maxWidth: '400px',
        margin: '1rem auto 0',
        padding: '0 1.5rem',
        boxSizing: 'border-box',
        overflowWrap: 'anywhere',
        wordBreak: 'break-word'
      }}>{error}</p>
    )}
  </div>
  </>
);
};

export default AuthForm;
